import React, { useState, useRef, useEffect } from 'react';
import { Camera, ShoppingCart, Trash2, Plus, ImageIcon, List, Check, X, MapPin, CheckCircle2, Circle, Grid3X3, FileText, Eye, Edit2, User, LogOut, LogIn, FolderOpen, Calendar } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Google Analytics tracking helper
const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

function App() {
  const [currentView, setCurrentView] = useState('meal-planner');
  const [shoppingList, setShoppingList] = useState([]);
  const [listId, setListId] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastAnalyzedItem, setLastAnalyzedItem] = useState(null);
  const [showAddManually, setShowAddManually] = useState(false);
  const [manualItemName, setManualItemName] = useState('');
  const [cameraError, setCameraError] = useState('');
  
  // Location and shopping mode state
  const [locationMode, setLocationMode] = useState(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [showTapPrompt, setShowTapPrompt] = useState(false);
  const [pendingCheckOff, setPendingCheckOff] = useState(null);
  const [manualShoppingMode, setManualShoppingMode] = useState(false);
  const [paperListMode, setPaperListMode] = useState(false);
  const [paperListResult, setPaperListResult] = useState(null);
  const [isProcessingPaperList, setIsProcessingPaperList] = useState(false);
  const [showWordSelection, setShowWordSelection] = useState(false);
  const [detectedWords, setDetectedWords] = useState([]);
  const [selectedWords, setSelectedWords] = useState([]);
  const [originalAnalysisResult, setOriginalAnalysisResult] = useState(null);
  const [showCheckOffConfirmation, setShowCheckOffConfirmation] = useState(false);
  const [matchingResults, setMatchingResults] = useState(null);
  const [displayMode, setDisplayMode] = useState('images'); // 'images', 'images_description', 'description'
  const [editablePaperListItems, setEditablePaperListItems] = useState([]);
  const [paperListWords, setPaperListWords] = useState([]); // Individual words for drag-drop
  const [draggedWordIndex, setDraggedWordIndex] = useState(null);
  const [editingWordIndex, setEditingWordIndex] = useState(null);
  const [editingWordText, setEditingWordText] = useState('');
  const [tapTimeout, setTapTimeout] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [showFruitImageContribution, setShowFruitImageContribution] = useState(false);
  const [fruitNeedingImage, setFruitNeedingImage] = useState(null);
  const [contributionImageBase64, setContributionImageBase64] = useState('');
  
  // Meal planning states
  const [showMealPlanner, setShowMealPlanner] = useState(false);
  const [mealPlanView, setMealPlanView] = useState('daily'); // 'daily', 'weekly', '2weekly'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [mealPlans, setMealPlans] = useState([]);
  const [similarities, setSimilarities] = useState([]);
  const [showAddMealDialog, setShowAddMealDialog] = useState(false);
  const [dailyViewLayout, setDailyViewLayout] = useState('columns'); // 'columns' or 'list'
  const [trackedSchools, setTrackedSchools] = useState(['School 1']);
  const [showSchoolManager, setShowSchoolManager] = useState(false);
  const [selectedMealDetail, setSelectedMealDetail] = useState(null);
  const [showMealDetailDialog, setShowMealDetailDialog] = useState(false);
  const [mealSimilarities, setMealSimilarities] = useState({});
  const [showRecipeViewer, setShowRecipeViewer] = useState(false);
  const [currentRecipeUrl, setCurrentRecipeUrl] = useState('');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [userShoppingLists, setUserShoppingLists] = useState([]);
  const [selectedExportListId, setSelectedExportListId] = useState('new');
  const [newListName, setNewListName] = useState('');
  const [newMealData, setNewMealData] = useState({
    date: '',
    mealType: 'breakfast',
    mealName: '',
    ingredients: '',
    isSchoolMenu: false,
    schoolName: '',
    recipeUrl: ''
  });
  const [recipeSearchQuery, setRecipeSearchQuery] = useState('');
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [showRecipeDropdown, setShowRecipeDropdown] = useState(false);
  const [showShoppingWordSelection, setShowShoppingWordSelection] = useState(false);
  const [shoppingModeAnalysisResult, setShoppingModeAnalysisResult] = useState(null);
  const [showSchoolSetup, setShowSchoolSetup] = useState(false);
  const [schoolInputValue, setSchoolInputValue] = useState('');
  const [hasSetupSchools, setHasSetupSchools] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [nearbySchools, setNearbySchools] = useState([]);
  const [selectedSchoolsForImport, setSelectedSchoolsForImport] = useState([]);
  const [searchRadius, setSearchRadius] = useState(5000); // 5km default
  const [locationViewMode, setLocationViewMode] = useState('list'); // 'list' or 'map'
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [schoolMenuOptions, setSchoolMenuOptions] = useState({}); // { school_id: { menus: [], loaded: bool } }
  const [draggedMenuItem, setDraggedMenuItem] = useState(null);
  
  // Screen width state for responsive design
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Authentication state
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  
  // Enhanced smart matching state
  const [showSmartMatchPopup, setShowSmartMatchPopup] = useState(false);
  const [smartMatchResults, setSmartMatchResults] = useState(null);
  const [showQuantityInput, setShowQuantityInput] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  
  // List management state
  const [currentListName, setCurrentListName] = useState('Weekly List');
  const [showListNameEditor, setShowListNameEditor] = useState(false);
  const [allUserLists, setAllUserLists] = useState([]);
  const [showListSelector, setShowListSelector] = useState(false);
  
  // My Lists functionality state
  const [savedLists, setSavedLists] = useState([]);
  const [sharedLists, setSharedLists] = useState([]);
  const [isLoadingSavedLists, setIsLoadingSavedLists] = useState(false);
  const [showMyLists, setShowMyLists] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  
  // Auto-save functionality  
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaveInterval, setAutoSaveInterval] = useState(null);

  // Fruit Recognition State (Multi-fruit with checkboxes)
  const [showFruitMatchDialog, setShowFruitMatchDialog] = useState(false);
  const [fruitMatches, setFruitMatches] = useState([]);
  const [selectedFruitMatches, setSelectedFruitMatches] = useState([]);
  const [fruitCapturedImage, setFruitCapturedImage] = useState('');
  const [fruitRecognitionMode, setFruitRecognitionMode] = useState('list_building');
  const [isMultiFruit, setIsMultiFruit] = useState(false);
  const [totalItemsDetected, setTotalItemsDetected] = useState(0);
  const [showContributeDialog, setShowContributeDialog] = useState(false);
  const [contributeFruitName, setContributeFruitName] = useState('');
  const [contributeFruitImage, setContributeFruitImage] = useState('');
  
  // New image naming state
  const [showNameNewImagePopup, setShowNameNewImagePopup] = useState(false);
  const [newImageData, setNewImageData] = useState(null);
  const [newImageName, setNewImageName] = useState('');
  
  // Recipe Transformer state
  const [showRecipeSearchDialog, setShowRecipeSearchDialog] = useState(false);
  const [recipeTransformerQuery, setRecipeTransformerQuery] = useState('');
  const [recipeSearchResults, setRecipeSearchResults] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeDetailDialog, setShowRecipeDetailDialog] = useState(false);
  const [showCustomizeDialog, setShowCustomizeDialog] = useState(false);
  const [customizationSliders, setCustomizationSliders] = useState({
    fat: 0,
    sugar: 0,
    salt: 0,
    carbs: 0,
    additives: 0
  });
  const [substitutionResults, setSubstitutionResults] = useState(null);
  const [userTargets, setUserTargets] = useState({
    fat_target: 0,
    sugar_target: 0,
    salt_target: 0,
    carbs_target: 0,
    additives_target: 0
  });
  const [showTargetsDialog, setShowTargetsDialog] = useState(false);
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  console.log('🚀 App rendered - Items:', shoppingList.length, 'ListID:', listId, 'ManualShopping:', manualShoppingMode, 'PaperList:', paperListMode, 'WordSelection:', showWordSelection, 'CheckOffConfirmation:', showCheckOffConfirmation, 'DisplayMode:', displayMode, 'EditablePaperItems:', editablePaperListItems.length, 'ShoppingWordSelection:', showShoppingWordSelection, 'SmartMatch:', showSmartMatchPopup, 'ListName:', currentListName);

  // Real-time updates: Poll for changes when viewing My Lists or when a list is loaded
  React.useEffect(() => {
    // Start polling when on My Lists view or when a list is loaded
    if (isAuthenticated && (currentView === 'my-lists' || listId)) {
      console.log('🔄 Starting real-time polling for list updates');
      
      const interval = setInterval(() => {
        // Refresh lists if on My Lists view
        if (currentView === 'my-lists') {
          loadSavedLists();
        }
        
        // Refresh current list if one is loaded
        if (listId) {
          refreshCurrentList();
        }
      }, 10000); // Poll every 10 seconds
      
      setPollingInterval(interval);
      
      // Cleanup
      return () => {
        if (interval) {
          clearInterval(interval);
          console.log('🛑 Stopped real-time polling');
        }
      };
    }
  }, [isAuthenticated, currentView, listId]);

  // Load meal plans when meal planner opens or date changes
  React.useEffect(() => {
    if (showMealPlanner && isAuthenticated) {
      const startDate = new Date(selectedDate);
      const endDate = new Date(selectedDate);
      
      if (mealPlanView === 'daily') {
        // Load current day
        endDate.setDate(endDate.getDate());
      } else if (mealPlanView === 'weekly') {
        // Load 7 days
        endDate.setDate(endDate.getDate() + 6);
      } else if (mealPlanView === '2weekly') {
        // Load 14 days
        endDate.setDate(endDate.getDate() + 13);
      }
      
      loadMealPlans(startDate, endDate);
    }
  }, [showMealPlanner, selectedDate, mealPlanView, isAuthenticated]);

  const refreshCurrentList = async () => {
    if (!listId) return;
    
    try {
      console.log('🔄 Refreshing current list from server');
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/${listId}`, {
        headers: {
          ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` })
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setShoppingList(data.items || []);
        console.log('✅ List refreshed with', data.items?.length || 0, 'items');
      }
    } catch (error) {
      console.error('❌ Error refreshing list:', error);
    }
  };

  // Enhanced smart matching functions
  const performSmartMatching = async (analysisResult, mode = 'list_building') => {
    if (!listId) return null;

    try {
      console.log(`🧠 Performing smart matching for ${mode} mode`);
      
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/smart-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scanned_text: analysisResult.extracted_text,
          list_id: listId,
          mode: mode
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🎯 Smart matching result:', result);
        return result;
      }
    } catch (error) {
      console.error('Error in smart matching:', error);
    }
    return null;
  };

  const handleSmartMatchDecision = async (decision, matchedItem = null, newItemData = null) => {
    console.log('🤔 Smart match decision:', decision, matchedItem, newItemData);

    try {
      switch (decision) {
        case 'add_quantity':
          if (matchedItem) {
            await addQuantityToItem(matchedItem.id, selectedQuantity);
            alert(`Added quantity ${selectedQuantity} to "${matchedItem.name}"`);
          }
          break;

        case 'add_new':
          if (newItemData) {
            // Capture image for new item
            let imageBase64 = null;
            try {
              imageBase64 = captureImage();
            } catch (error) {
              console.error('Could not capture image for new item:', error);
            }
            
            await addItemToList(
              newItemData.name,
              newItemData.extracted_text,
              imageBase64,
              newItemData.selected_words || []
            );
            alert(`Added "${newItemData.name}" as a new item`);
          }
          break;

        case 'swap_existing':
          if (matchedItem) {
            // Uncheck the existing item and check the new one
            await uncheckItem(matchedItem.id);
            
            // Add new item with word selection
            setSmartMatchResults({
              ...smartMatchResults,
              showWordSelection: true
            });
            return; // Don't close popup yet
          }
          break;

        case 'cancel':
          // Just close the popup
          break;
      }
    } catch (error) {
      console.error('Error handling smart match decision:', error);
      alert('Error processing your choice. Please try again.');
    }

    // Close popups
    setShowSmartMatchPopup(false);
    setSmartMatchResults(null);
    setShowQuantityInput(false);
    setSelectedQuantity(1);
  };

  const addQuantityToItem = async (itemId, quantity) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/add-quantity?list_id=${listId}&item_id=${itemId}&quantity=${quantity}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Update local state
        setShoppingList(prev => prev.map(item => 
          item.id === itemId ? { ...item, quantity: (item.quantity || 1) + quantity } : item
        ));
        return result;
      }
    } catch (error) {
      console.error('Error adding quantity:', error);
      throw error;
    }
  };

  // List management functions
  const saveListWithName = async (name) => {
    if (!listId) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/save-with-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` })
        },
        body: JSON.stringify({
          list_id: listId,
          name: name
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentListName(name);
        alert(`List saved as "${name}"`);
        return result;
      }
    } catch (error) {
      console.error('Error saving list name:', error);
      alert('Error saving list name. Please try again.');
    }
  };

  const loadAllUserLists = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/all`, {
        headers: {
          ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        setAllUserLists(result.lists || []);
        return result.lists;
      }
    } catch (error) {
      console.error('Error loading user lists:', error);
    }
    return [];
  };

  // My Lists functionality functions
  const loadSavedLists = async () => {
    if (!isAuthenticated) {
      console.log('⚠️ User not authenticated, cannot load saved lists');
      return;
    }

    setIsLoadingSavedLists(true);
    try {
      console.log('📋 Loading saved lists for authenticated user');
      
      // Load user's own lists
      const ownListsResponse = await fetch(`${BACKEND_URL}/api/shopping-list/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      if (ownListsResponse.ok) {
        const ownListsData = await ownListsResponse.json();
        setSavedLists(ownListsData.lists || []);
      }

      // Load shared lists
      const sharedListsResponse = await fetch(`${BACKEND_URL}/api/shopping-list/shared`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      if (sharedListsResponse.ok) {
        const sharedListsData = await sharedListsResponse.json();
        setSharedLists(sharedListsData.shared_lists || []);
      }

    } catch (error) {
      console.error('Error loading saved lists:', error);
      alert('Error loading saved lists. Please try again.');
    } finally {
      setIsLoadingSavedLists(false);
    }
  };

  const loadShoppingList = async (selectedListId) => {
    if (!isAuthenticated) {
      alert('Please login to load saved lists');
      return;
    }

    try {
      console.log(`📥 Loading shopping list: ${selectedListId}`);
      
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/load`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify({
          list_id: selectedListId
        })
      });

      if (response.ok) {
        const result = await response.json();
        const loadedList = result.list;
        
        // Update current list state
        setShoppingList(loadedList.items || []);
        setListId(loadedList.id);
        setCurrentListName(loadedList.name || 'Weekly List');
        
        // Switch to list view
        setCurrentView('list');
        setShowMyLists(false);
        
        alert(`✅ Loaded list: ${loadedList.name}`);
        
      } else {
        throw new Error('Failed to load list');
      }
    } catch (error) {
      console.error('Error loading shopping list:', error);
      alert('Error loading shopping list. Please try again.');
    }
  };

  const autoSaveList = async () => {
    if (!isAuthenticated || !listId || shoppingList.length === 0) {
      return;
    }

    try {
      console.log('💾 Auto-saving list...');
      
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/auto-save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify({
          list_id: listId,
          items: shoppingList
        })
      });

      if (response.ok) {
        const result = await response.json();
        setLastAutoSave(new Date());
        console.log('✅ List auto-saved successfully');
      }
    } catch (error) {
      console.error('Error auto-saving list:', error);
      // Don't show error to user for auto-save failures
    }
  };

  const shareList = async () => {
    if (!isAuthenticated || !listId || !shareEmail.trim()) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      console.log(`🤝 Sharing list with: ${shareEmail}`);
      
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify({
          list_id: listId,
          email: shareEmail.trim(),
          permission: sharePermission
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ List shared with ${shareEmail}`);
        setShareEmail('');
        setShowShareDialog(false);
        
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to share list');
      }
    } catch (error) {
      console.error('Error sharing list:', error);
      alert(`Error sharing list: ${error.message}`);
    }
  };

  // Fruit Recognition Functions
  const recognizeFruit = async (imageBase64) => {
    try {
      console.log('🍎 Starting fruit recognition...');
      console.log('📊 Image size:', imageBase64 ? `${Math.round(imageBase64.length / 1024)}KB` : 'No image');
      
      const mode = manualShoppingMode ? 'shopping' : 'list_building';
      console.log('🎯 Recognition mode:', mode);
      console.log('🆔 List ID:', listId);
      
      const response = await fetch(`${BACKEND_URL}/api/fruit/recognize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` })
        },
        body: JSON.stringify({
          image_base64: imageBase64,
          list_id: listId,
          mode: mode
        })
      });

      console.log('📡 API Response status:', response.status);
      const result = await response.json();
      console.log('📋 API Response data:', result);
      
      if (result.success) {
        console.log(`✅ Recognition successful - Found ${result.matches?.length || 0} matches`);
        return result;
      } else {
        console.error('❌ Recognition failed:', result.detail);
        throw new Error(result.detail || 'Recognition failed');
      }
      
    } catch (error) {
      console.error('❌ Error recognizing fruit:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  };

  const contributeFruit = async (imageBase64, fruitName) => {
    if (!isAuthenticated) {
      alert('Please login to contribute fruits to the database');
      return;
    }

    try {
      console.log(`🤝 Contributing fruit: ${fruitName}`);
      
      const response = await fetch(`${BACKEND_URL}/api/fruit/contribute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify({
          image_base64: imageBase64,
          fruit_name: fruitName,
          list_id: listId
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(`🎉 Thanks for contributing! You earned ${result.points_earned} points. Total: ${result.total_points}`);
        
        // If item was added to list, reload the list to show the new item
        if (result.fruit_added && listId) {
          console.log('✅ Reloading shopping list to show contributed item...');
          // Fetch the updated shopping list
          const listResponse = await fetch(`${BACKEND_URL}/api/shopping-list/${listId}`, {
            headers: {
              ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` })
            }
          });
          
          if (listResponse.ok) {
            const listData = await listResponse.json();
            setShoppingList(listData.items || []);
            console.log('✅ Shopping list updated with contributed item');
          } else {
            console.error('❌ Failed to reload shopping list');
          }
        }
        
        return result;
      } else {
        throw new Error(result.detail || 'Contribution failed');
      }
      
    } catch (error) {
      console.error('Error contributing fruit:', error);
      alert(`Error contributing fruit: ${error.message}`);
      throw error;
    }
  };

  // Authentication functions
  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        setIsLoadingAuth(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setUser(data.user);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('session_token');
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        localStorage.removeItem('session_token');
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoadingAuth(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      alert('Please enter email and password');
      return;
    }

    try {
      const endpoint = isRegistering ? '/api/auth/register' : '/api/auth/login';
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginEmail,
          password: loginPassword,
          name: isRegistering ? loginEmail.split('@')[0] : undefined
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store session token
        localStorage.setItem('session_token', data.session_token);
        
        // Update state
        setUser(data.user);
        setIsAuthenticated(true);
        setShowLoginForm(false);
        setLoginEmail('');
        setLoginPassword('');
        
        // Track successful login
        trackEvent('login', {
          method: isRegistering ? 'register' : 'login',
          user_id: data.user.id
        });
        
        console.log('✅ Authentication successful:', data.user);
      } else {
        alert(data.detail || 'Authentication failed');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      alert('Authentication failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (token) {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
      }

      // Clear local state
      localStorage.removeItem('session_token');
      setUser(null);
      setIsAuthenticated(false);
      
      console.log('✅ Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if logout API fails
      localStorage.removeItem('session_token');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const handleAuthCallback = async () => {
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/profile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ session_id: sessionId })
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('session_token', data.session_token);
          setUser(data.user);
          setIsAuthenticated(true);
          
          // Track successful login
          trackEvent('login', {
            method: 'oauth_callback',
            user_id: data.user.id
          });
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          alert(`Welcome, ${data.user.name}! You are now logged in.`);
        } else {
          throw new Error('Authentication failed');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        alert('Authentication failed. Please try again.');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  };



  // Initialize camera and location detection
  useEffect(() => {
    initializeCamera();
    detectUserLocation();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Initialize authentication
  useEffect(() => {
    checkAuthStatus();
    handleAuthCallback();
  }, []);

  // Handle screen width changes for responsive design
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsMobile(width < 768);
      console.log('📱 Screen width changed:', width, 'Mobile:', width < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reinitialize camera when switching to camera view to ensure it's always available
  useEffect(() => {
    if (currentView === 'camera') {
      console.log('🔄 Camera view activated - reinitializing camera...');
      setTimeout(() => {
        initializeCamera();
      }, 300); // Short delay to allow DOM to update
    }
  }, [currentView]);

  // Auto-save functionality - save when shopping list changes (only for authenticated users)
  useEffect(() => {
    if (isAuthenticated && listId && shoppingList.length > 0) {
      // Clear any existing auto-save timer
      if (autoSaveInterval) {
        clearTimeout(autoSaveInterval);
      }
      
      // Set up new auto-save timer (debounced)
      const timer = setTimeout(() => {
        autoSaveList();
      }, 2000); // Auto-save 2 seconds after last change
      
      setAutoSaveInterval(timer);
      
      // Cleanup function
      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, [shoppingList, isAuthenticated, listId]);

  const initializeCamera = async () => {
    try {
      console.log('🎥 Initializing camera...');
      setCameraError('');
      
      // Stop any existing stream first
      if (streamRef.current) {
        console.log('🛑 Stopping existing camera stream');
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported on this device');
      }
      
      // Request camera access
      console.log('📷 Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      console.log('✅ Camera access granted, setting up video stream');
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          console.log('📹 Video stream ready');
        };
        
        console.log('✅ Camera initialized successfully');
      } else {
        console.error('❌ Video element not found');
        throw new Error('Video element not available');
      }
    } catch (error) {
      console.error('❌ Camera access error:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Unable to access camera. ';
      
      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please grant camera permissions and refresh the page.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += 'Camera is being used by another application.';
      } else if (error.message.includes('API not supported')) {
        errorMessage += 'Camera functionality not supported on this device.';
      } else {
        errorMessage += 'Please ensure camera permissions are granted and try again.';
      }
      
      setCameraError(errorMessage);
    }
  };

  const detectUserLocation = async () => {
    setIsDetectingLocation(true);
    try {
      // Get user's GPS location
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        });
      });

      const { latitude, longitude } = position.coords;
      setUserLocation({ latitude, longitude });

      // Check shopping mode with backend
      const response = await fetch(`${BACKEND_URL}/api/detect-location-mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          latitude,
          longitude,
          radius: 100
        }),
      });

      if (response.ok) {
        const locationData = await response.json();
        setLocationMode(locationData);
        console.log('📍 Location mode detected:', locationData);
      }
    } catch (error) {
      console.error('Location detection error:', error);
      // Default to list building mode
      setLocationMode({
        is_shopping_mode: false,
        mode: "list_building",
        confidence: 0.0,
        nearby_stores: [],
        current_location: null
      });
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.log('⚠️ Video or canvas ref not available for image capture');
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Check if video is ready and has valid dimensions
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      console.log('⚠️ Video stream not ready or has invalid dimensions');
      return null;
    }

    try {
      const ctx = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0);

      return canvas.toDataURL('image/jpeg', 0.8);
    } catch (error) {
      console.error('Error capturing image:', error);
      return null;
    }
  };

  const analyzeImage = async () => {
    if (isAnalyzing) return;

    // Check if we're in a mode transition or camera is not ready
    if (!videoRef.current || !canvasRef.current || !streamRef.current) {
      console.log('⏳ Camera not ready for image analysis, skipping...');
      return;
    }

    // Additional check for video stream readiness
    if (videoRef.current.readyState < 2) { // HAVE_CURRENT_DATA = 2
      console.log('⏳ Video stream not ready, skipping image analysis...');
      return;
    }

    setIsAnalyzing(true);
    setLastAnalyzedItem(null);
    setShowTapPrompt(false);
    setPendingCheckOff(null);

    try {
      const imageDataUrl = captureImage();
      if (!imageDataUrl) {
        console.log('⚠️ No image captured during analysis, possibly due to mode transition');
        return;
      }

      // Convert data URL to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();

      // Create form data
      const formData = new FormData();
      formData.append('file', blob, 'captured_image.jpg');

      // Analyze image
      const analysisResponse = await fetch(`${BACKEND_URL}/api/analyze-image`, {
        method: 'POST',
        body: formData,
      });

      if (!analysisResponse.ok) {
        throw new Error('Failed to analyze image');
      }

      const result = await analysisResponse.json();
      console.log('📷 Image analysis result:', result);
      
      // Check if any text was detected
      const hasText = result.text_annotations && result.text_annotations.length > 0;
      
      if (!hasText) {
        console.log('🍎 No text detected - trying fruit recognition...');
        
        // Convert image to base64 for fruit recognition
        const imageBase64 = imageDataUrl.split(',')[1];
        
        try {
          const fruitResult = await recognizeFruit(imageBase64);
          
          if (fruitResult.success && fruitResult.matches && fruitResult.matches.length > 0) {
            // Track fruit recognition success
            trackEvent('fruit_recognition', {
              matches_found: fruitResult.matches.length,
              total_items: fruitResult.total_items_detected || 1,
              is_multi_fruit: fruitResult.is_multi_fruit,
              top_match: fruitResult.matches[0]?.fruit_name,
              top_confidence: fruitResult.matches[0]?.confidence,
              mode: fruitResult.mode
            });
            
            // Show fruit match selection dialog with multi-fruit support
            setFruitMatches(fruitResult.matches);
            setSelectedFruitMatches([]);
            setFruitCapturedImage(imageBase64);
            setFruitRecognitionMode(fruitResult.mode || 'list_building');
            setIsMultiFruit(fruitResult.is_multi_fruit || false);
            setTotalItemsDetected(fruitResult.total_items_detected || 1);
            setShowFruitMatchDialog(true);
            return;
          } else {
            // No matches found - show contribution dialog
            setContributeFruitImage(imageBase64);
            setShowContributeDialog(true);
            return;
          }
        } catch (error) {
          console.error('Fruit recognition failed:', error);
        }
        
        // If fruit recognition also fails, show standard "no text" message
        alert('No text or recognizable fruits/vegetables detected in the image. Please try with a clearer image or use manual input.');
        return;
      }
      
      // Handle different modes when text is detected
      const isInShoppingMode = manualShoppingMode || (locationMode?.is_shopping_mode && listId);
      
      if (isInShoppingMode && listId) {
        // Shopping mode: check if item exists
        await handleShoppingModeAnalysis(result);
      } else if (paperListMode) {
        // Paper list mode: should not reach here, handled separately
        return;
      } else {
        // List building mode: use splitText for word selection
        splitText(result);
      }

    } catch (error) {
      console.error('Error analyzing image:', error);
      // Only show error if it's not due to mode transition
      if (videoRef.current && canvasRef.current && streamRef.current) {
        alert('Error analyzing image. Please try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const findMatchingItems = (scannedWords, shoppingList) => {
    console.log('🔍 Finding matching items for scanned words:', scannedWords);
    
    const matches = [];
    
    for (const listItem of shoppingList) {
      if (listItem.checked_off) continue; // Skip already checked off items
      
      // Extract words from the list item name
      const itemWords = listItem.name
        .toLowerCase()
        .split(/[\s\-_,.']+/)
        .filter(word => word.length >= 2);
      
      console.log(`📋 Checking item "${listItem.name}" words:`, itemWords);
      
      // Check if any scanned word matches any item word
      const matchingWords = scannedWords.filter(scannedWord => 
        itemWords.some(itemWord => 
          itemWord.includes(scannedWord.toLowerCase()) || 
          scannedWord.toLowerCase().includes(itemWord)
        )
      );
      
      if (matchingWords.length > 0) {
        matches.push({
          item: listItem,
          matchingWords: matchingWords,
          confidence: matchingWords.length / Math.max(scannedWords.length, itemWords.length)
        });
        console.log(`✅ Match found: "${listItem.name}" (${matchingWords.join(', ')})`);
      }
    }
    
    // Sort by confidence (highest first)
    matches.sort((a, b) => b.confidence - a.confidence);
    
    console.log(`🎯 Found ${matches.length} matching items`);
    return matches;
  };

  const handleShoppingModeAnalysis = async (analysisResult) => {
    if (!listId) return;

    try {
      console.log('🛒 Starting enhanced word matching analysis...');
      console.log('📝 Extracted text:', analysisResult.extracted_text);
      
      // Use the new word matching endpoint
      const matchResponse = await fetch(`${BACKEND_URL}/api/shopping-list/match-words`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scanned_text: analysisResult.extracted_text,
          list_id: listId
        }),
      });

      if (matchResponse.ok) {
        const matchResult = await matchResponse.json();
        console.log('🔍 Word matching result:', matchResult);
        
        if (matchResult.matches && matchResult.matches.length > 0) {
          // Found matches - show confirmation popup
          setMatchingResults({
            matches: matchResult.matches,
            scannedText: analysisResult.extracted_text,
            productName: analysisResult.product_name
          });
          setShowCheckOffConfirmation(true);
        } else {
          // No matches found - show naming popup for new image
          console.log('❌ No word matches found - showing naming popup for new image');
          
          // Capture the current image for storage
          let imageBase64 = null;
          try {
            imageBase64 = captureImage();
            console.log('📷 Captured image for new item naming:', !!imageBase64);
          } catch (error) {
            console.error('⚠️ Could not capture image for new item:', error);
          }
          
          // Store the data and show naming popup
          setNewImageData({
            productName: analysisResult.product_name,
            extractedText: analysisResult.extracted_text,
            imageBase64: imageBase64,
            mode: 'shopping' // This came from shopping mode
          });
          setNewImageName(analysisResult.product_name || '');
          setShowNameNewImagePopup(true);
        }
      }
    } catch (error) {
      console.error('Error in enhanced shopping mode analysis:', error);
      // Fallback to adding the item normally
      setLastAnalyzedItem(analysisResult);
    }
  };

  const addItemToList = async (itemName, extractedText = '', imageBase64 = null, selectedWords = []) => {
    console.log('📝 Adding item to list:', itemName);
    console.log('🖼️ Has image:', !!imageBase64);
    console.log('🔤 Selected words:', selectedWords);
    
    try {
      const url = `${BACKEND_URL}/api/shopping-list/items${listId ? `?list_id=${listId}` : ''}`;
      console.log('🌐 API URL:', url);
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if user is authenticated
      const token = localStorage.getItem('session_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          name: itemName,
          extracted_text: extractedText,
          image_base64: imageBase64,
          selected_words: selectedWords
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Item added successfully:', result);
      
      // Track item addition event
      trackEvent('add_item', {
        item_name: result.item.name,
        method: extractedText ? 'ocr_scan' : 'manual',
        has_image: !!imageBase64,
        list_size: shoppingList.length + 1
      });
      
      // Set list ID if this is the first item
      if (!listId) {
        setListId(result.list_id);
      }

      // Update local shopping list
      setShoppingList(prev => [...prev, result.item]);
      setLastAnalyzedItem(null);
      setManualItemName('');
      setShowAddManually(false);

      return result;

    } catch (error) {
      console.error('❌ Error adding item:', error);
      alert(`Error adding item: ${error.message}`);
      return null;
    }
  };

  const checkOffItem = async (itemId) => {
    if (!listId) return;

    try {
      console.log('✅ Checking off item:', itemId);
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/check-off`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          item_id: itemId,
          list_id: listId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check off item');
      }

      // Update local state
      setShoppingList(prev => 
        prev.map(item => 
          item.id === itemId 
            ? { ...item, checked_off: true, checked_off_at: new Date().toISOString() }
            : item
        )
      );

      console.log('✅ Item checked off successfully');
      
      // Track check-off event
      trackEvent('item_check_off', {
        item_id: itemId,
        shopping_mode: manualShoppingMode,
        remaining_items: shoppingList.filter(item => item.id !== itemId && !item.checked_off).length
      });

    } catch (error) {
      console.error('Error checking off item:', error);
      alert('Error checking off item. Please try again.');
    }
  };

  const uncheckItem = async (itemId) => {
    if (!listId) return;

    try {
      console.log('⭕ Unchecking item:', itemId);
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/uncheck`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          item_id: itemId,
          list_id: listId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to uncheck item');
      }

      // Update local state
      setShoppingList(prev => 
        prev.map(item => 
          item.id === itemId 
            ? { ...item, checked_off: false, checked_off_at: null }
            : item
        )
      );

      console.log('⭕ Item unchecked successfully');

    } catch (error) {
      console.error('Error unchecking item:', error);
      alert('Error unchecking item. Please try again.');
    }
  };

  const removeItem = async (itemId) => {
    if (!listId) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/${listId}/items/${itemId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      // Update local shopping list
      setShoppingList(prev => prev.filter(item => item.id !== itemId));

    } catch (error) {
      console.error('Error removing item:', error);
      alert('Error removing item. Please try again.');
    }
  };

  const findWordMatchesForSplitText = (scannedWords, shoppingList) => {
    console.log('🔍 Finding word matches for splitText in shopping mode');
    console.log('📝 Scanned words:', scannedWords);
    
    const matches = [];
    
    for (const listItem of shoppingList) {
      if (listItem.checked_off) continue; // Skip already checked off items
      
      // Extract words from both the item name and selected_words array
      const itemWords = listItem.name
        .toLowerCase()
        .split(/[\s\-_,.']+/)
        .filter(word => word.length >= 2);
      
      // Add selected words if they exist
      const selectedWords = listItem.selected_words || [];
      const selectedWordsLower = selectedWords.map(word => word.toLowerCase().trim()).filter(word => word.length >= 2);
      
      const allItemWords = [...itemWords, ...selectedWordsLower];
      const uniqueItemWords = [...new Set(allItemWords)];
      
      console.log(`🏷️ Item "${listItem.name}" words (including selected):`, uniqueItemWords);
      
      // Find matching words
      const matchingWords = [];
      for (const scannedWord of scannedWords) {
        const scannedLower = scannedWord.toLowerCase();
        for (const itemWord of uniqueItemWords) {
          if (scannedLower === itemWord || 
              scannedLower.includes(itemWord) || 
              itemWord.includes(scannedLower)) {
            if (!matchingWords.includes(scannedWord)) {
              matchingWords.push(scannedWord);
            }
          }
        }
      }
      
      if (matchingWords.length > 0) {
        // Calculate confidence based on match ratio
        const confidence = matchingWords.length / Math.max(scannedWords.length, uniqueItemWords.length);
        
        const match = {
          item: listItem,
          matching_words: matchingWords,
          confidence: Math.round(confidence * 100) / 100, // Round to 2 decimal places
          item_words: uniqueItemWords,
          scanned_words: scannedWords,
          selected_words: listItem.selected_words || []
        };
        matches.push(match);
        console.log(`✅ Match found: "${listItem.name}" with words ${matchingWords} (confidence: ${confidence.toFixed(2)})`);
      }
    }
    
    // Sort by confidence (highest first)
    matches.sort((a, b) => b.confidence - a.confidence);
    
    console.log(`🎯 Total splitText matches found: ${matches.length}`);
    return matches;
  };

  const confirmShoppingWordSelection = async () => {
    if (selectedWords.length === 0) return;
    
    const selectedItemName = selectedWords.join(' ');
    console.log('✅ Shopping mode word selection confirmed:', selectedItemName, 'Words:', selectedWords);
    
    // Capture the current image from the camera for storage
    let imageBase64 = null;
    try {
      imageBase64 = captureImage();
      console.log('📷 Captured image for shopping mode item:', !!imageBase64);
    } catch (error) {
      console.error('⚠️ Could not capture image for shopping item:', error);
    }
    
    // Add item with selected words and image
    const addResult = await addItemToList(
      selectedItemName, 
      shoppingModeAnalysisResult?.extracted_text || '', 
      imageBase64,
      selectedWords
    );
    
    if (addResult) {
      // Immediately check off the newly added item since we're in shopping mode
      await checkOffItem(addResult.item.id);
      console.log('✅ Item added and checked off in shopping mode');
      
      // Show success message
      alert(`Added "${addResult.item.name}" to your list and checked it off!`);
    }
    
    // Reset shopping word selection state
    setShowShoppingWordSelection(false);
    setSelectedWords([]);
    setDetectedWords([]);
    setShoppingModeAnalysisResult(null);
  };

  const cancelShoppingWordSelection = () => {
    console.log('❌ Shopping mode word selection cancelled');
    setShowShoppingWordSelection(false);
    setSelectedWords([]);
    setDetectedWords([]);
    setShoppingModeAnalysisResult(null);
  };

  const handleNoMatchesInShopping = async (analysisResult, scannedWords) => {
    console.log('❌ No matches found in shopping mode - showing word selection popup');
    
    // Show splitText word selection popup for shopping mode
    setShoppingModeAnalysisResult(analysisResult);
    setDetectedWords(scannedWords);
    setSelectedWords([]);
    setShowShoppingWordSelection(true);
    
    console.log('✅ Shopping mode word selection popup opened with', scannedWords.length, 'words');
  };

  const splitText = (analysisResult) => {
    console.log('🔤 Starting enhanced splitText with smart matching');
    console.log('📝 Analysis result:', analysisResult);
    console.log('🛒 Current mode - ManualShopping:', manualShoppingMode, 'LocationShopping:', locationMode?.is_shopping_mode);
    
    if (!analysisResult || !analysisResult.extracted_text) {
      console.log('⚠️ No extracted text available for splitting');
      return;
    }

    const text = analysisResult.extracted_text;
    console.log('📄 Raw extracted text:', text);

    // Split the text into words and clean them
    const words = text
      .split(/[\s\n\r\t,.-]+/)
      .map(word => word.trim())
      .filter(word => word.length >= 2)
      .filter(word => !/^\d+$/.test(word)) // Remove pure numbers
      .filter(word => !/^[^\w]+$/.test(word)) // Remove pure punctuation
      .map(word => word.replace(/[^\w\s'-]/g, '')) // Clean remaining punctuation except apostrophes and hyphens
      .filter(word => word.length >= 2) // Filter again after cleaning
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize
      .filter((word, index, arr) => arr.indexOf(word) === index) // Remove duplicates
      .slice(0, 20); // Limit to 20 words for UI performance

    console.log('🔤 Processed words:', words);
    
    if (words.length === 0) {
      console.log('⚠️ No valid words found after processing');
      alert('No recognizable words found in the image. Please try again.');
      return;
    }
    
    // Check if we're in shopping mode and have a list to match against
    const isInShoppingMode = manualShoppingMode || (locationMode?.is_shopping_mode && listId);
    
    // Perform smart matching first for both modes
    performSmartMatching(analysisResult, isInShoppingMode ? 'shopping' : 'list_building')
      .then(smartMatchResult => {
        if (smartMatchResult && smartMatchResult.matches && smartMatchResult.matches.length > 0) {
          console.log('🎯 Smart matches found, showing smart match popup');
          setSmartMatchResults({
            matches: smartMatchResult.matches,
            scannedText: analysisResult.extracted_text,
            productName: analysisResult.product_name,
            mode: smartMatchResult.mode,
            scannedWords: words,
            analysisResult: analysisResult
          });
          setShowSmartMatchPopup(true);
        } else {
          console.log('❌ No smart matches found, proceeding with regular flow');
          // No smart matches found - proceed with original logic
          if (isInShoppingMode) {
            // Shopping mode - show word selection for new item
            handleNoMatchesInShopping(analysisResult, words);
          } else {
            // List building mode - show word selection popup
            setOriginalAnalysisResult(analysisResult);
            setDetectedWords(words);
            setSelectedWords([]);
            setShowWordSelection(true);
            console.log('✅ Word selection popup opened with', words.length, 'words');
          }
        }
      })
      .catch(error => {
        console.error('Error in smart matching, falling back to regular flow:', error);
        // Fallback to original logic
        if (isInShoppingMode) {
          handleNoMatchesInShopping(analysisResult, words);
        } else {
          setOriginalAnalysisResult(analysisResult);
          setDetectedWords(words);
          setSelectedWords([]);
          setShowWordSelection(true);
        }
      });
  };

  const toggleWordSelection = (word) => {
    console.log('🔘 Toggling word selection:', word);
    setSelectedWords(prev => {
      if (prev.includes(word)) {
        const newSelection = prev.filter(w => w !== word);
        console.log('➖ Removed word, new selection:', newSelection);
        return newSelection;
      } else {
        const newSelection = [...prev, word];
        console.log('➕ Added word, new selection:', newSelection);
        return newSelection;
      }
    });
  };

  const confirmWordSelection = async () => {
    if (selectedWords.length === 0) return;
    
    const selectedItemName = selectedWords.join(' ');
    console.log('✅ Confirmed word selection:', selectedItemName, 'Words:', selectedWords);
    
    // Capture the current image from the camera for storage
    let imageBase64 = null;
    try {
      imageBase64 = captureImage();
      console.log('📷 Captured image for storage:', !!imageBase64);
    } catch (error) {
      console.error('⚠️ Could not capture image for storage:', error);
    }
    
    // Check if this might be a new item worth naming (if it has an image and seems unique)
    const shouldShowNaming = imageBase64 && selectedWords.length <= 3 && 
                           !selectedItemName.toLowerCase().includes('unknown');
    
    if (shouldShowNaming) {
      // Show naming popup for potential new shared item
      setNewImageData({
        productName: selectedItemName,
        extractedText: originalAnalysisResult?.extracted_text || '',
        imageBase64: imageBase64,
        selectedWords: selectedWords,
        mode: 'list_building'
      });
      setNewImageName(selectedItemName);
      setShowNameNewImagePopup(true);
      
      // Reset word selection state
      setShowWordSelection(false);
      setSelectedWords([]);
      setDetectedWords([]);
      setOriginalAnalysisResult(null);
    } else {
      // Add item directly without naming popup
      await addItemToList(
        selectedItemName, 
        originalAnalysisResult?.extracted_text || '', 
        imageBase64,
        selectedWords
      );
      
      // Reset word selection state
      setShowWordSelection(false);
      setSelectedWords([]);
      setDetectedWords([]);
      setOriginalAnalysisResult(null);
    }
  };

  const cancelWordSelection = () => {
    console.log('❌ Canceling word selection');
    setShowWordSelection(false);
    setDetectedWords([]);
    setSelectedWords([]);
    setOriginalAnalysisResult(null);
  };

  // New image naming functions
  const handleNameNewImage = async () => {
    if (!newImageName.trim() || !newImageData) {
      alert('Please enter a name for the item');
      return;
    }

    console.log('🏷️ Naming new image item:', newImageName);

    try {
      // First, create the shared item in database
      const sharedItemResponse = await fetch(`${BACKEND_URL}/api/shared-items/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` })
        },
        body: JSON.stringify({
          name: newImageName.trim(),
          category: classifyItemCategory(newImageName),
          description: `User-named item: ${newImageName}`,
          selected_words: [newImageName.trim()],
          image_base64: newImageData.imageBase64
        })
      });

      if (sharedItemResponse.ok) {
        const sharedResult = await sharedItemResponse.json();
        console.log('✅ Created shared item:', sharedResult.shared_item_id);
      }

      // Add to user's shopping list
      const addResult = await addItemToList(
        newImageName.trim(),
        newImageData.extractedText || '',
        newImageData.imageBase64,
        [newImageName.trim()]
      );

      if (addResult) {
        // If this was from shopping mode, check it off automatically
        if (newImageData.mode === 'shopping') {
          await checkOffItem(addResult.item.id);
          alert(`Added "${newImageName}" to your list and checked it off!`);
        } else {
          alert(`Added "${newImageName}" to your list!`);
        }
      }

    } catch (error) {
      console.error('Error naming new image:', error);
      alert('Error saving the item. Please try again.');
    }

    // Close the popup
    setShowNameNewImagePopup(false);
    setNewImageData(null);
    setNewImageName('');
  };

  const cancelNameNewImage = () => {
    console.log('❌ Canceling new image naming');
    setShowNameNewImagePopup(false);
    setNewImageData(null);
    setNewImageName('');
  };

  const classifyItemCategory = (itemName) => {
    const name = itemName.toLowerCase();
    
    // Fruits
    const fruits = ['apple', 'banana', 'orange', 'grape', 'strawberry', 'blueberry', 'pear', 
                  'peach', 'plum', 'cherry', 'kiwi', 'mango', 'pineapple', 'watermelon', 
                  'cantaloupe', 'berry', 'fruit'];
    
    // Vegetables  
    const vegetables = ['carrot', 'broccoli', 'spinach', 'lettuce', 'tomato', 'cucumber',
                      'pepper', 'onion', 'garlic', 'potato', 'celery', 'cabbage', 'kale',
                      'vegetable', 'veggie'];
    
    // Check for matches
    if (fruits.some(fruit => name.includes(fruit))) {
      return 'fruits';
    } else if (vegetables.some(veg => name.includes(veg))) {
      return 'vegetables';
    } else {
      return 'packaged_goods';
    }
  };

  const startPaperListImport = () => {
    console.log('📄 Starting paper list import mode');
    setPaperListMode(true);
    setPaperListResult(null);
    setCurrentView('camera');
    alert('Paper list import mode activated! Point your camera at a handwritten shopping list.');
  };

  const stopPaperListImport = () => {
    console.log('📄 Stopping paper list import mode');
    setPaperListMode(false);
    setPaperListResult(null);
    setIsProcessingPaperList(false);
    setEditablePaperListItems([]);
    setPaperListWords([]);
    
    // Reinitialize camera when exiting paper list mode
    setTimeout(() => {
      console.log('🔄 Reinitializing camera after exiting paper list mode...');
      initializeCamera();
    }, 500);
  };

  const processPaperListImage = async () => {
    if (isProcessingPaperList) return;

    setIsProcessingPaperList(true);
    setPaperListResult(null);

    try {
      const imageDataUrl = captureImage();
      if (!imageDataUrl) {
        throw new Error('Failed to capture image');
      }

      // Convert data URL to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();

      // Create form data
      const formData = new FormData();
      formData.append('file', blob, 'paper_list.jpg');

      console.log('📄 Processing paper list image...');

      // Process paper list
      const ocrResponse = await fetch(`${BACKEND_URL}/api/import-paper-list`, {
        method: 'POST',
        body: formData,
      });

      if (!ocrResponse.ok) {
        throw new Error('Failed to process paper list');
      }

      const result = await ocrResponse.json();
      console.log('📄 Paper list OCR result:', result);
      
      setPaperListResult(result);
      
      // Initialize with individual words for drag-drop UI
      if (result.success && result.items) {
        // Convert items to word objects with grouping info
        const words = result.items.map((item, itemIndex) => ({
          text: item,
          groupId: itemIndex,
          id: `word-${itemIndex}`
        }));
        setPaperListWords(words);
        setEditablePaperListItems([...result.items]);
      } else {
        setPaperListWords([]);
        setEditablePaperListItems([]);
      }

    } catch (error) {
      console.error('Error processing paper list:', error);
      alert('Error processing paper list. Please try again.');
    } finally {
      setIsProcessingPaperList(false);
    }
  };

  const importPaperListItems = async (selectedItems) => {
    try {
      console.log('📥 Importing items:', selectedItems);

      const response = await fetch(`${BACKEND_URL}/api/shopping-list/import-items${listId ? `?list_id=${listId}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(selectedItems),
      });

      if (!response.ok) {
        throw new Error('Failed to import items');
      }

      const result = await response.json();
      console.log('📥 Import result:', result);

      // Set list ID if this is the first import
      if (!listId) {
        setListId(result.list_id);
      }

      // Update local shopping list
      setShoppingList(prev => [...prev, ...result.items]);
      
      // Clean up paper list mode
      setPaperListMode(false);
      setPaperListResult(null);
      setEditablePaperListItems([]);
      setPaperListWords([]);
      
      alert(`Successfully imported ${result.items_added} items from your paper list!`);

    } catch (error) {
      console.error('Error importing items:', error);
      alert('Error importing items. Please try again.');
    }
  };

  const handleEditPaperListItem = (index, newValue) => {
    const updatedItems = [...editablePaperListItems];
    updatedItems[index] = newValue;
    setEditablePaperListItems(updatedItems);
  };

  const handleDeletePaperListItem = (index) => {
    const updatedItems = editablePaperListItems.filter((_, i) => i !== index);
    setEditablePaperListItems(updatedItems);
  };

  const handleAddNewPaperListItem = () => {
    setEditablePaperListItems([...editablePaperListItems, 'New Item']);
  };

  // Drag and drop functions for paper list words
  const handleWordDragStart = (index) => {
    setDraggedWordIndex(index);
  };

  const handleWordDragOver = (e, targetIndex) => {
    e.preventDefault();
  };

  const handleWordDrop = (targetIndex) => {
    if (draggedWordIndex === null || draggedWordIndex === targetIndex) {
      setDraggedWordIndex(null);
      return;
    }

    const updatedWords = [...paperListWords];
    const draggedWord = updatedWords[draggedWordIndex];
    const targetWord = updatedWords[targetIndex];
    
    // Group the dragged word with the target word (same groupId)
    draggedWord.groupId = targetWord.groupId;
    
    // Reorder: remove from old position and insert next to target
    updatedWords.splice(draggedWordIndex, 1);
    
    // Find the new position of target after removal
    const newTargetIndex = updatedWords.findIndex(w => w.id === targetWord.id);
    const insertIndex = draggedWordIndex < targetIndex ? newTargetIndex + 1 : newTargetIndex;
    
    updatedWords.splice(insertIndex, 0, draggedWord);
    
    setPaperListWords(updatedWords);
    setDraggedWordIndex(null);
  };

  const handleWordDragEnd = () => {
    setDraggedWordIndex(null);
  };

  // Word editing functions
  const handleWordClick = (index, text) => {
    // Clear any existing tap timeout
    if (tapTimeout) {
      clearTimeout(tapTimeout);
      setTapTimeout(null);
    }

    // Set a timeout to distinguish single tap from double tap
    const timeout = setTimeout(() => {
      // Single tap - enter edit mode
      setEditingWordIndex(index);
      setEditingWordText(text);
      setTapTimeout(null);
    }, 300);
    
    setTapTimeout(timeout);
  };

  const handleWordDoubleClick = (index) => {
    // Clear the single tap timeout
    if (tapTimeout) {
      clearTimeout(tapTimeout);
      setTapTimeout(null);
    }
    
    // Double tap - separate word
    separateWord(index);
  };

  const saveWordEdit = () => {
    if (editingWordIndex !== null && editingWordText.trim()) {
      const updatedWords = [...paperListWords];
      updatedWords[editingWordIndex].text = editingWordText.trim();
      setPaperListWords(updatedWords);
    }
    setEditingWordIndex(null);
    setEditingWordText('');
  };

  const cancelWordEdit = () => {
    setEditingWordIndex(null);
    setEditingWordText('');
  };

  // Load recipes from backend
  const loadRecipes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/meal-plan/recipes/search?limit=100`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Clean recipe titles - remove dashes and format
          const cleanedRecipes = data.recipes.map(recipe => ({
            ...recipe,
            title: recipe.title.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
          }));
          setAvailableRecipes(cleanedRecipes);
          setFilteredRecipes(cleanedRecipes);
          console.log('✅ Loaded', cleanedRecipes.length, 'recipes');
        }
      }
    } catch (error) {
      console.error('Error loading recipes:', error);
    }
  };

  // Filter recipes in real-time
  const handleRecipeSearch = (query) => {
    setRecipeSearchQuery(query);
    setNewMealData({...newMealData, mealName: query});
    
    if (query.length > 0) {
      const filtered = availableRecipes.filter(recipe =>
        recipe.title.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredRecipes(filtered);
      setShowRecipeDropdown(true);
    } else {
      setFilteredRecipes(availableRecipes);
      setShowRecipeDropdown(false);
    }
  };

  // Select a recipe from dropdown
  const selectRecipe = (recipe) => {
    // Use cleaned ingredients instead of raw
    const cleanedIngredients = recipe.clean_ingredients || recipe.ingredients;
    
    setNewMealData({
      ...newMealData,
      mealName: recipe.title,
      ingredients: cleanedIngredients.join('\n'),
      recipeUrl: recipe.url || ''
    });
    setRecipeSearchQuery(recipe.title);
    setShowRecipeDropdown(false);
  };

  // Load meal plans for date range
  const loadMealPlans = async (startDate, endDate) => {
    try {
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];
      
      const response = await fetch(
        `${BACKEND_URL}/api/meal-plan/get?start_date=${start}&end_date=${end}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMealPlans(data.meal_plans || []);
        console.log('📅 Loaded', data.meal_plans?.length || 0, 'meal plans');
      }
    } catch (error) {
      console.error('Error loading meal plans:', error);
    }
  };

  // Delete meal plan
  const handleDeleteMeal = async () => {
    if (!selectedMealDetail || !selectedMealDetail.id) {
      alert('❌ No meal selected');
      return;
    }

    if (!confirm(`Are you sure you want to delete "${selectedMealDetail.meal_name}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/meal-plan/delete/${selectedMealDetail.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`
          }
        }
      );

      if (response.ok) {
        alert('✅ Meal deleted successfully!');
        
        // Remove from local state
        setMealPlans(prevPlans => prevPlans.filter(meal => meal.id !== selectedMealDetail.id));
        
        // Close dialog
        setShowMealDetailDialog(false);
        setSelectedMealDetail(null);
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.detail || 'Failed to delete meal'}`);
      }
    } catch (error) {
      console.error('Error deleting meal:', error);
      alert('❌ Error deleting meal. Please try again.');
    }
  };

  // Load user's shopping lists
  const loadUserShoppingLists = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shopping-list/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserShoppingLists(data.lists || []);
      }
    } catch (error) {
      console.error('Error loading shopping lists:', error);
    }
  };

  // Export ingredients to shopping list
  const handleExportIngredients = async () => {
    if (!selectedMealDetail || !selectedMealDetail.ingredients) {
      alert('❌ No ingredients to export');
      return;
    }

    // Load user's lists when opening dialog
    await loadUserShoppingLists();
    setShowExportDialog(true);
  };

  const confirmExportIngredients = async () => {
    if (!selectedMealDetail) return;

    try {
      let targetListId = selectedExportListId;

      // If creating new list, create it first
      if (selectedExportListId === 'new') {
        if (!newListName.trim()) {
          alert('⚠️ Please enter a name for the new list');
          return;
        }

        // Create new list
        const createResponse = await fetch(`${BACKEND_URL}/api/shopping-list/save-with-name`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`
          },
          body: JSON.stringify({
            list_id: null,
            name: newListName
          })
        });

        if (createResponse.ok) {
          const createData = await createResponse.json();
          targetListId = createData.list_id;
        } else {
          alert('❌ Failed to create new list');
          return;
        }
      }

      // Add ingredients as items
      const ingredients = selectedMealDetail.ingredients || selectedMealDetail.clean_ingredients || [];
      let successCount = 0;
      let failCount = 0;

      for (const ingredient of ingredients) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/shopping-list/items?list_id=${targetListId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('session_token')}`
            },
            body: JSON.stringify({
              name: ingredient,
              selected_words: [ingredient]
            })
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error adding ingredient ${ingredient}:`, error);
          failCount++;
        }
      }

      // Close dialogs and show result
      setShowExportDialog(false);
      setShowMealDetailDialog(false);
      setSelectedExportListId('new');
      setNewListName('');

      if (successCount > 0) {
        alert(`✅ Successfully exported ${successCount} ingredient${successCount !== 1 ? 's' : ''} to shopping list!${failCount > 0 ? `\n⚠️ ${failCount} failed` : ''}`);
        
        // If we're on the main shopping list view and it's the current list, reload it
        if (targetListId === listId) {
          await loadShoppingList(listId);
        }
      } else {
        alert('❌ Failed to export ingredients. Please try again.');
      }
    } catch (error) {
      console.error('Error exporting ingredients:', error);
      alert('❌ Error exporting ingredients. Please try again.');
    }
  };

  // Navigate dates
  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  // School setup functions
  const handleSchoolSetup = async () => {
    if (!schoolInputValue.trim()) {
      alert('⚠️ Please enter at least one school name');
      return;
    }

    // Split by comma and clean up
    const schoolNames = schoolInputValue
      .split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    if (schoolNames.length === 0) {
      alert('⚠️ Please enter valid school names');
      return;
    }

    // Query database for each school name
    const newSchools = [];
    
    for (const name of schoolNames) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/schools/search?query=${encodeURIComponent(name)}&limit=1`);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.schools && data.schools.length > 0) {
            // Use the school from database
            const dbSchool = data.schools[0];
            newSchools.push({
              name: dbSchool.name,
              town: dbSchool.town,
              postcode: dbSchool.postcode,
              id: dbSchool.id,
              color: '#' + Math.floor(Math.random()*16777215).toString(16)
            });
          } else {
            // School not found in database, add as text only
            newSchools.push({
              name: name,
              color: '#' + Math.floor(Math.random()*16777215).toString(16)
            });
          }
        }
      } catch (error) {
        console.error('Error searching for school:', error);
        // Fallback to text-only school
        newSchools.push({
          name: name,
          color: '#' + Math.floor(Math.random()*16777215).toString(16)
        });
      }
    }
    
    setTrackedSchools([...trackedSchools, ...newSchools]);
    
    // Save to localStorage
    localStorage.setItem('trackedSchools', JSON.stringify([...trackedSchools, ...newSchools]));
    localStorage.setItem('hasSetupSchools', 'true');
    
    // Load menus for schools with IDs
    newSchools.forEach(school => {
      if (school.id) {
        loadSchoolMenus(school.id);
      }
    });
    
    // Clear input and close setup
    setSchoolInputValue('');
    setShowSchoolSetup(false);
    setHasSetupSchools(true);
    setNearbySchools([]); // Clear autocomplete

    alert(`✅ Added ${schoolNames.length} school${schoolNames.length > 1 ? 's' : ''}: ${schoolNames.join(', ')}`);
  };

  // Check if user has setup schools on mount
  React.useEffect(() => {
    const savedSetup = localStorage.getItem('hasSetupSchools');
    const savedSchools = localStorage.getItem('trackedSchools');
    
    if (savedSetup === 'true' && savedSchools) {
      setHasSetupSchools(true);
      try {
        const schools = JSON.parse(savedSchools);
        setTrackedSchools(schools);
        // Load menus for tracked schools
        schools.forEach(school => {
          if (school.id) {
            loadSchoolMenus(school.id);
          }
        });
      } catch (e) {
        console.error('Error parsing saved schools:', e);
      }
    }
  }, []);

  // Load school menus from database
  const loadSchoolMenus = async (schoolId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/menus/${schoolId}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.has_menus) {
          setSchoolMenuOptions(prev => ({
            ...prev,
            [schoolId]: {
              menus: data.menus || [],
              standardized_menus: data.standardized_menus || [],
              loaded: true,
              school_name: data.school_name
            }
          }));
          console.log(`📋 Loaded ${data.total_menus} menus for ${data.school_name}`);
          if (data.standardized_menus && data.standardized_menus.length > 0) {
            console.log(`   ✅ ${data.standardized_menus.length} standardized menus (day-by-day)`);
          }
        } else {
          setSchoolMenuOptions(prev => ({
            ...prev,
            [schoolId]: {
              menus: [],
              standardized_menus: [],
              loaded: true,
              school_name: ''
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error loading school menus:', error);
    }
  };

  // Import school menus to meal planner for 2 weeks
  const importSchoolMenusToPlanner = async (schoolId, schoolName) => {
    if (!isAuthenticated) {
      alert('⚠️ Please sign in to import school menus');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/import-menus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          alert(`✅ ${data.message}\n\n📅 Imported ${data.imported_count} lunch options for the next 2 weeks.`);
          
          // Reload meal plans
          const start = new Date(selectedDate);
          start.setDate(start.getDate() - 7);
          const end = new Date(selectedDate);
          end.setDate(end.getDate() + 14);
          await loadMealPlans(start, end);
        } else {
          alert(`⚠️ ${data.message}`);
        }
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.detail || 'Failed to import menus'}`);
      }
    } catch (error) {
      console.error('Error importing school menus:', error);
      alert('❌ Error importing school menus. Please try again.');
    }
  };

  // Location-based school search functions
  const getUserLocation = () => {
    setLoadingLocation(true);
    
    if (!navigator.geolocation) {
      alert('❌ Geolocation is not supported by your browser');
      setLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(location);
        searchNearbySchools(location.lat, location.lng, searchRadius);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('❌ Unable to get your location. Please check your browser permissions.');
        setLoadingLocation(false);
      }
    );
  };

  const searchNearbySchools = async (lat, lng, radius) => {
    try {
      // For now, use database search by showing all schools
      // In future, could filter by distance if we add geocoding
      const response = await fetch(`${BACKEND_URL}/api/schools/search?query=&limit=100`);

      if (response.ok) {
        const data = await response.json();
        setNearbySchools(data.schools || []);
        setShowLocationPicker(true);
        setLoadingLocation(false);
        console.log(`📍 Found ${data.schools.length} schools in database`);
      } else {
        alert('❌ Error searching for schools. Please try again.');
        setLoadingLocation(false);
      }
    } catch (error) {
      console.error('Error searching nearby schools:', error);
      alert('❌ Error searching for schools. Please try again.');
      setLoadingLocation(false);
    }
  };

  const searchSchoolsByText = async (query) => {
    if (!query || query.length < 2) {
      setNearbySchools([]);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/search?query=${encodeURIComponent(query)}&limit=20`);
      
      if (response.ok) {
        const data = await response.json();
        setNearbySchools(data.schools || []);
        console.log(`🔍 Found ${data.schools.length} schools matching "${query}"`);
      }
    } catch (error) {
      console.error('Error searching schools:', error);
    }
  };

  const handleRadiusChange = (newRadius) => {
    setSearchRadius(newRadius);
    if (userLocation) {
      searchNearbySchools(userLocation.lat, userLocation.lng, newRadius);
    }
  };

  const toggleSchoolSelection = (school) => {
    const isSelected = selectedSchoolsForImport.some(s => s.id === school.id);
    
    if (isSelected) {
      setSelectedSchoolsForImport(selectedSchoolsForImport.filter(s => s.id !== school.id));
    } else {
      setSelectedSchoolsForImport([...selectedSchoolsForImport, school]);
    }
  };

  const importSelectedSchools = () => {
    if (selectedSchoolsForImport.length === 0) {
      alert('⚠️ Please select at least one school to import');
      return;
    }

    // Add selected schools to tracked list
    const newSchools = selectedSchoolsForImport.map(school => ({
      name: school.name,
      town: school.town,
      postcode: school.postcode,
      id: school.id,
      color: '#' + Math.floor(Math.random()*16777215).toString(16)
    }));

    setTrackedSchools([...trackedSchools, ...newSchools]);
    localStorage.setItem('trackedSchools', JSON.stringify([...trackedSchools, ...newSchools]));
    localStorage.setItem('hasSetupSchools', 'true');
    setHasSetupSchools(true);

    // Load menus for newly imported schools
    newSchools.forEach(school => {
      if (school.id) {
        loadSchoolMenus(school.id);
      }
    });

    // Close dialog and reset
    setShowLocationPicker(false);
    setSelectedSchoolsForImport([]);
    setNearbySchools([]);
    
    alert(`✅ Imported ${newSchools.length} school${newSchools.length > 1 ? 's' : ''}!\n\n${newSchools.map(s => s.name).join('\n')}`);
  };

  // Drag and drop handlers for menu options
  const handleDragStart = (e, menuItem, schoolId) => {
    setDraggedMenuItem({ ...menuItem, source_school_id: schoolId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropToChoice = async (e, schoolId, mealType) => {
    e.preventDefault();
    
    if (!draggedMenuItem) return;

    try {
      // Find the school object
      const school = trackedSchools.find(s => s.id === schoolId);
      
      // Create meal plan from the dropped menu option
      const requestBody = {
        date: selectedDate.toISOString().split('T')[0],
        meal_type: mealType,
        meal_name: draggedMenuItem.meal_name || draggedMenuItem,
        ingredients: draggedMenuItem.ingredients || [],
        is_school_menu: true,
        school_name: school?.name || 'Unknown School',
        recipe_url: null
      };

      const response = await fetch(`${BACKEND_URL}/api/meal-plan/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        // Reload meal plans
        const start = new Date(selectedDate);
        const end = new Date(selectedDate);
        await loadMealPlans(start, end);
        
        console.log('✅ Menu option added to choice column');
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.detail || 'Failed to add meal'}`);
      }
    } catch (error) {
      console.error('Error adding menu to choice:', error);
      alert('❌ Error adding menu option. Please try again.');
    }

    setDraggedMenuItem(null);
  };

  const handleDropToOptions = async (e, schoolId) => {
    e.preventDefault();
    
    if (!draggedMenuItem || !draggedMenuItem.meal_id) return;

    try {
      // Delete the meal from the choice column
      const response = await fetch(`${BACKEND_URL}/api/meal-plan/delete/${draggedMenuItem.meal_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      if (response.ok) {
        // Reload meal plans
        const start = new Date(selectedDate);
        const end = new Date(selectedDate);
        await loadMealPlans(start, end);
        
        console.log('✅ Meal removed from choice column');
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.detail || 'Failed to remove meal'}`);
      }
    } catch (error) {
      console.error('Error removing meal:', error);
      alert('❌ Error removing meal. Please try again.');
    }

    setDraggedMenuItem(null);
  };

  // Get meals for specific date
  const getMealsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return mealPlans.filter(meal => meal.date === dateStr);
  };

  // Get meals by type and school
  const getMealsByTypeAndSchool = (date, mealType, isSchool, schoolName = null) => {
    const meals = getMealsForDate(date);
    return meals.filter(meal => {
      if (meal.meal_type !== mealType) return false;
      if (isSchool) {
        return meal.is_school_menu && (!schoolName || meal.school_name === schoolName);
      }
      return !meal.is_school_menu;
    });
  };

  // Calculate similarity for a meal within 48 hours
  const calculateMealSimilarity = (meal, allMeals) => {
    const mealDate = new Date(meal.date);
    const mealIngredients = meal.clean_ingredients || [];
    
    // Find all meals within 48 hours
    const nearbyMeals = allMeals.filter(m => {
      if (m.id === meal.id) return false;
      const otherDate = new Date(m.date);
      const hoursDiff = Math.abs((mealDate - otherDate) / (1000 * 60 * 60));
      return hoursDiff <= 48;
    });
    
    if (nearbyMeals.length === 0) return 0;
    
    // Calculate similarity with each nearby meal
    let maxSimilarity = 0;
    
    nearbyMeals.forEach(otherMeal => {
      const otherIngredients = otherMeal.clean_ingredients || [];
      
      // Simple Jaccard similarity
      const set1 = new Set(mealIngredients.map(i => i.toLowerCase()));
      const set2 = new Set(otherIngredients.map(i => i.toLowerCase()));
      
      const intersection = new Set([...set1].filter(x => set2.has(x)));
      const union = new Set([...set1, ...set2]);
      
      if (union.size > 0) {
        const similarity = (intersection.size / union.size) * 100;
        maxSimilarity = Math.max(maxSimilarity, similarity);
      }
    });
    
    return Math.round(maxSimilarity);
  };

  // Get color tier based on similarity
  const getSimilarityColor = (similarity) => {
    if (similarity < 25) return 'bg-green-100 border-green-300 text-green-900';
    if (similarity <= 75) return 'bg-amber-100 border-amber-300 text-amber-900';
    return 'bg-red-100 border-red-300 text-red-900';
  };

  // Get similarity badge
  const getSimilarityBadge = (similarity) => {
    if (similarity < 25) return '🟢';
    if (similarity <= 75) return '🟠';
    return '🔴';
  };

  // ====================================================================================
  // RECIPE TRANSFORMER FUNCTIONS
  // ====================================================================================

  // Search for recipes (Recipe Transformer)
  const handleTransformerRecipeSearch = async () => {
    if (!recipeTransformerQuery.trim()) {
      alert('Please enter a recipe name');
      return;
    }

    setIsLoadingRecipes(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/recipes/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: recipeTransformerQuery,
          limit: 5
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setRecipeSearchResults(data.recipes || data.results);
        console.log('✅ Found recipes:', data.recipes?.length || data.results?.length);
      } else {
        alert('Failed to search recipes');
      }
    } catch (error) {
      console.error('Recipe search error:', error);
      alert('Error searching recipes');
    } finally {
      setIsLoadingRecipes(false);
    }
  };

  // Select a recipe
  const handleSelectRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeDetailDialog(true);
    setShowRecipeSearchDialog(false);
  };

  // Save recipe to user account
  const handleSaveRecipe = async () => {
    if (!selectedRecipe) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/recipes/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify({
          recipe: selectedRecipe,
          customized: false,
          adjustments: null
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`✅ Recipe "${selectedRecipe.name}" saved to your account!`);
      } else {
        alert('Failed to save recipe');
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert('Error saving recipe');
    }
  };

  // Start customization
  const handleStartCustomization = () => {
    // Load user targets if available
    loadUserTargets();
    setShowRecipeDetailDialog(false);
    setShowCustomizeDialog(true);
  };

  // Load user targets
  const loadUserTargets = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/targets`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      const data = await response.json();
      
      if (data.success && data.has_set_targets) {
        setUserTargets(data.targets);
        setCustomizationSliders({
          fat: data.targets.fat_target || 0,
          sugar: data.targets.sugar_target || 0,
          salt: data.targets.salt_target || 0,
          carbs: data.targets.carbs_target || 0,
          additives: data.targets.additives_target || 0
        });
      }
    } catch (error) {
      console.error('Error loading targets:', error);
    }
  };

  // Save user targets
  const handleSaveTargets = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/targets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify(userTargets)
      });

      const data = await response.json();
      
      if (data.success) {
        alert('✅ Reduction targets saved!');
        setShowTargetsDialog(false);
        
        // Apply to sliders
        setCustomizationSliders({
          fat: userTargets.fat_target,
          sugar: userTargets.sugar_target,
          salt: userTargets.salt_target,
          carbs: userTargets.carbs_target,
          additives: userTargets.additives_target
        });
      }
    } catch (error) {
      console.error('Error saving targets:', error);
      alert('Error saving targets');
    }
  };

  // Apply customization and get substitutions
  const handleApplyCustomization = async () => {
    if (!selectedRecipe) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/recipes/customize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify({
          recipe_id: selectedRecipe.recipe_id,
          adjustments: customizationSliders
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSubstitutionResults(data);
        console.log('✅ Customization applied:', data);
        alert(`✅ NPM Score improved from ${data.original_npm} to ${data.new_npm}! Generated ${data.substitutions.length} suggestions.`);
      } else {
        alert('Failed to customize recipe');
      }
    } catch (error) {
      console.error('Error customizing recipe:', error);
      alert('Error customizing recipe');
    }
  };

  // Get color based on HFSS status
  const getHFSSColor = (color) => {
    switch(color) {
      case 'green':
        return 'bg-green-500';
      case 'amber':
        return 'bg-amber-500';
      case 'red':
        return 'bg-red-500';
      default:
        return 'bg-gray-300';
    }
  };

  // Get slider color based on current value and nutrient color
  const getSliderColor = (nutrient, value) => {
    if (!selectedRecipe || !selectedRecipe.health_scores) return 'bg-gray-200';
    
    const originalColor = selectedRecipe.health_scores.hfss_colors[nutrient];
    
    // If original was red
    if (originalColor === 'red') {
      if (value >= 50) return 'bg-green-200 border-green-400';
      if (value >= 25) return 'bg-amber-200 border-amber-400';
      return 'bg-red-200 border-red-400';
    }
    
    // If original was amber
    if (originalColor === 'amber') {
      if (value >= 30) return 'bg-green-200 border-green-400';
      return 'bg-amber-200 border-amber-400';
    }
    
    // If original was green
    return 'bg-green-200 border-green-400';
  };

  const groupWordsByLine = () => {
    // Group consecutive words into lines/items
    const items = [];
    let currentItem = [];
    
    paperListWords.forEach((word, index) => {
      currentItem.push(word.text);
      
      // Check if next word should start a new line (you can add logic here)
      // For now, treat each word as potentially separate
      const nextWord = paperListWords[index + 1];
      if (!nextWord || word.groupId !== nextWord.groupId) {
        items.push(currentItem.join(' '));
        currentItem = [];
      }
    });
    
    if (currentItem.length > 0) {
      items.push(currentItem.join(' '));
    }
    
    return items;
  };

  const mergeWords = (index1, index2) => {
    // Merge two adjacent words into same group
    const updatedWords = [...paperListWords];
    const minIndex = Math.min(index1, index2);
    const maxIndex = Math.max(index1, index2);
    
    // Set same groupId for both
    const newGroupId = updatedWords[minIndex].groupId;
    updatedWords[maxIndex].groupId = newGroupId;
    
    setPaperListWords(updatedWords);
  };

  const separateWord = (index) => {
    // Give this word its own unique group
    const updatedWords = [...paperListWords];
    updatedWords[index].groupId = Date.now() + index;
    setPaperListWords(updatedWords);
  };

  const getGroupedWords = () => {
    // Group words by their groupId for display
    const groups = {};
    paperListWords.forEach((word, index) => {
      if (!groups[word.groupId]) {
        groups[word.groupId] = [];
      }
      groups[word.groupId].push({ ...word, originalIndex: index });
    });
    return Object.values(groups);
  };

  const handleImportGroupedWords = () => {
    const items = getGroupedWords().map(group => 
      group.map(w => w.text).join(' ')
    ).filter(item => item.trim().length > 0);
    
    if (items.length > 0) {
      importPaperListItems(items);
    } else {
      alert('Please arrange words into items first');
    }
  };

  const handleImportEditedItems = () => {
    const validItems = editablePaperListItems.filter(item => item.trim().length > 0);
    if (validItems.length > 0) {
      importPaperListItems(validItems);
    } else {
      alert('Please add at least one item to import.');
    }
  };

  const startManualShopping = () => {
    if (shoppingList.length === 0) {
      alert('Please add some items to your shopping list first!');
      return;
    }
    
    console.log('🛒 Starting manual shopping mode');
    console.log('📹 Camera stream status:', streamRef.current ? 'Active' : 'Inactive');
    console.log('🎥 Video element exists:', !!videoRef.current);
    console.log('🏷️ Current camera error:', cameraError);
    
    setManualShoppingMode(true);
    setCurrentView('camera');
    
    // Track shopping mode start
    trackEvent('shopping_mode_start', {
      list_size: shoppingList.length,
      unchecked_items: shoppingList.filter(item => !item.checked_off).length
    });
    
    // Reinitialize camera when entering manual shopping mode
    console.log('🔄 Reinitializing camera for shopping mode...');
    setTimeout(() => {
      initializeCamera();
    }, 500);
    
    alert('Shopping mode activated! Point your camera at items to check them off your list.');
  };

  const stopManualShopping = () => {
    console.log('🏠 Stopping manual shopping mode');
    setManualShoppingMode(false);
    setShowTapPrompt(false);
    setPendingCheckOff(null);
    
    // Reinitialize camera when exiting shopping mode to ensure it's available
    setTimeout(() => {
      console.log('🔄 Reinitializing camera after exiting shopping mode...');
      initializeCamera();
    }, 500);
  };

  const handleAddManually = () => {
    console.log('🚀 Manual add clicked! Item name:', manualItemName);
    console.log('🔍 Current state - Items:', shoppingList.length, 'ListID:', listId);
    
    if (manualItemName.trim()) {
      console.log('✅ Calling addItemToList with:', manualItemName.trim());
      addItemToList(manualItemName.trim());
    } else {
      console.log('❌ Item name is empty');
      alert('Please enter an item name');
    }
  };

  const handleScreenTap = async () => {
    if (!showTapPrompt || !pendingCheckOff) return;

    console.log('👆 Screen tapped - checking off item');
    await checkOffItem(pendingCheckOff.item.id);
    setShowTapPrompt(false);
    setPendingCheckOff(null);
  };

  const handleConfirmCheckOff = async (selectedItem) => {
    console.log('✅ Confirming check off for item:', selectedItem);
    await checkOffItem(selectedItem.id);
    setShowCheckOffConfirmation(false);
    setMatchingResults(null);
  };

  const handleDenyCheckOff = async () => {
    console.log('❌ User denied check off - adding as new item');
    
    if (matchingResults) {
      // Capture the current image for storage
      let imageBase64 = null;
      try {
        imageBase64 = captureImage();
        console.log('📷 Captured image for denied check off item:', !!imageBase64);
      } catch (error) {
        console.error('⚠️ Could not capture image for denied item:', error);
      }
      
      // Add the scanned item as a new item and check it off
      const addResult = await addItemToList(
        matchingResults.productName, 
        matchingResults.scannedText,
        imageBase64,
        [matchingResults.productName] // Use product name as selected word
      );
      if (addResult) {
        await checkOffItem(addResult.item.id);
        alert(`Added "${addResult.item.name}" as a new item and checked it off!`);
      }
    }
    
    setShowCheckOffConfirmation(false);
    setMatchingResults(null);
  };

  const getShoppingModeStatus = () => {
    if (manualShoppingMode) {
      return {
        isActive: true,
        mode: 'shopping',
        source: 'manual',
        confidence: 100,
        message: 'Manual Shopping Mode'
      };
    } else if (locationMode?.is_shopping_mode) {
      return {
        isActive: true,
        mode: 'shopping',
        source: 'gps',
        confidence: Math.round(locationMode.confidence * 100),
        message: 'GPS Shopping Mode'
      };
    } else {
      return {
        isActive: false,
        mode: 'list_building',
        source: 'none',
        confidence: 0,
        message: 'List Building Mode'
      };
    }
  };

  const getCheckedOffCount = () => {
    return shoppingList.filter(item => item.checked_off).length;
  };

  const getUncheckedCount = () => {
    return shoppingList.filter(item => !item.checked_off).length;
  };

  const renderDisplayModeButtons = () => {
    return (
      <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
        <Button
          onClick={() => setDisplayMode('images')}
          variant={displayMode === 'images' ? 'default' : 'ghost'}
          size="sm"
          className="flex items-center space-x-1 px-2 py-1"
        >
          <Grid3X3 className="w-4 h-4" />
          <span className="hidden sm:inline">Images</span>
        </Button>
        <Button
          onClick={() => setDisplayMode('images_description')}
          variant={displayMode === 'images_description' ? 'default' : 'ghost'}
          size="sm"
          className="flex items-center space-x-1 px-2 py-1"
        >
          <Eye className="w-4 h-4" />
          <span className="hidden sm:inline">Both</span>
        </Button>
        <Button
          onClick={() => setDisplayMode('description')}
          variant={displayMode === 'description' ? 'default' : 'ghost'}
          size="sm"
          className="flex items-center space-x-1 px-2 py-1"
        >
          <FileText className="w-4 h-4" />
          <span className="hidden sm:inline">Text</span>
        </Button>
      </div>
    );
  };

  const renderItemsByDisplayMode = (items) => {
    const checkedItems = items.filter(item => item.checked_off);
    const uncheckedItems = items.filter(item => !item.checked_off);

    if (displayMode === 'images') {
      return (
        <div className="space-y-4">
          {uncheckedItems.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Items to Get</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {uncheckedItems.map((item) => (
                  <div
                    key={item.id}
                    className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-square bg-gray-50 flex items-center justify-center">
                      {item.image_base64 ? (
                        <img
                          src={`data:image/jpeg;base64,${item.image_base64}`}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        onClick={() => checkOffItem(item.id)}
                        variant="ghost"
                        size="sm"
                        className="p-1 h-6 w-6 bg-white shadow-sm hover:bg-green-50"
                      >
                        <Check className="w-4 h-4 text-green-600" />
                      </Button>
                    </div>
                    <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        onClick={() => removeItem(item.id)}
                        variant="ghost"
                        size="sm"
                        className="p-1 h-6 w-6 bg-white shadow-sm hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </Button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      <p className="text-white text-xs font-medium truncate">{item.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {checkedItems.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">In Basket</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {checkedItems.map((item) => (
                  <div
                    key={item.id}
                    className="relative group bg-white border border-green-200 rounded-lg overflow-hidden opacity-75"
                  >
                    <div className="aspect-square bg-gray-50 flex items-center justify-center">
                      {item.image_base64 ? (
                        <img
                          src={`data:image/jpeg;base64,${item.image_base64}`}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div className="absolute top-2 right-2">
                      <div className="p-1 h-6 w-6 bg-green-100 rounded shadow-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        onClick={() => uncheckItem(item.id)}
                        variant="ghost"
                        size="sm"
                        className="p-1 h-6 w-6 bg-white shadow-sm hover:bg-orange-50"
                      >
                        <X className="w-3 h-3 text-orange-600" />
                      </Button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      <p className="text-white text-xs font-medium truncate line-through">{item.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    } else if (displayMode === 'images_description') {
      return (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${
                item.checked_off 
                  ? 'bg-green-50 border-green-200 opacity-75' 
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                {item.image_base64 ? (
                  <img
                    src={`data:image/jpeg;base64,${item.image_base64}`}
                    alt={item.name}
                    className="w-full h-full object-cover rounded-lg"
                    loading="lazy"
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <p className={`font-medium ${item.checked_off ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                  {item.name}
                </p>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span>Added {new Date(item.added_at).toLocaleTimeString()}</span>
                  {item.checked_off && item.checked_off_at && (
                    <>
                      <span>•</span>
                      <span className="text-green-600">
                        Checked off {new Date(item.checked_off_at).toLocaleTimeString()}
                      </span>
                    </>
                  )}
                </div>
                {item.selected_words && item.selected_words.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {item.selected_words.map((word, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {word}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => item.checked_off ? uncheckItem(item.id) : checkOffItem(item.id)}
                  variant="ghost"
                  size="sm"
                  className="p-1 h-8 w-8"
                >
                  {item.checked_off ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400" />
                  )}
                </Button>
                <Button
                  onClick={() => removeItem(item.id)}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      // Description mode - original layout
      return (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                item.checked_off 
                  ? 'bg-green-50 border-green-200 opacity-75' 
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Button
                  onClick={() => item.checked_off ? uncheckItem(item.id) : checkOffItem(item.id)}
                  variant="ghost"
                  size="sm"
                  className="p-1 h-8 w-8"
                >
                  {item.checked_off ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400" />
                  )}
                </Button>
                <div>
                  <p className={`font-medium ${item.checked_off ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                    {item.name}
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span>Added {new Date(item.added_at).toLocaleTimeString()}</span>
                    {item.checked_off && item.checked_off_at && (
                      <>
                        <span>•</span>
                        <span className="text-green-600">
                          Checked off {new Date(item.checked_off_at).toLocaleTimeString()}
                        </span>
                      </>
                    )}
                  </div>
                  {item.selected_words && item.selected_words.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.selected_words.map((word, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {word}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <Button
                onClick={() => removeItem(item.id)}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 w-full overflow-x-hidden" onClick={showTapPrompt ? handleScreenTap : undefined}>
      {/* Smart Match Popup */}
      {showSmartMatchPopup && smartMatchResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {smartMatchResults.mode === 'shopping' 
                  ? '🛒 Smart Shopping Match Found!' 
                  : '🧠 Smart List Match Found!'
                }
              </h3>
              <p className="text-sm text-gray-600">
                {smartMatchResults.mode === 'shopping'
                  ? 'Found items that match your scan. What would you like to do?'
                  : 'Found similar items on your list. What would you like to do?'
                }
              </p>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="space-y-3">
                {smartMatchResults.matches.map((match, index) => (
                  <div key={match.item.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 mb-2">
                          {match.item.name}
                          {match.item.checked_off && (
                            <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                              Already in basket
                            </span>
                          )}
                          {match.item.quantity && match.item.quantity > 1 && (
                            <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                              Qty: {match.item.quantity}
                            </span>
                          )}
                        </p>
                        
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="flex flex-wrap gap-1">
                            {match.matching_words.map((word, wordIndex) => (
                              <span key={wordIndex} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                {word}
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-gray-500">
                            {Math.round(match.confidence * 100)}% match
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {smartMatchResults.mode === 'shopping' ? (
                            <>
                              {match.item.checked_off ? (
                                <Button
                                  onClick={() => handleSmartMatchDecision('swap_existing', match.item)}
                                  size="sm"
                                  className="bg-orange-600 hover:bg-orange-700"
                                >
                                  🔄 Swap This Item
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => handleSmartMatchDecision('check_off', match.item)}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  ✅ Check Off
                                </Button>
                              )}
                              
                              <Button
                                onClick={() => {
                                  setShowQuantityInput(true);
                                }}
                                size="sm"
                                variant="outline"
                                className="border-blue-600 text-blue-600"
                              >
                                ➕ Add Quantity
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                onClick={() => {
                                  setShowQuantityInput(true);
                                }}
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                ➕ Add Quantity
                              </Button>
                            </>
                          )}
                        </div>

                        {showQuantityInput && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium">Quantity:</span>
                              <input
                                type="number"
                                min="1"
                                max="99"
                                value={selectedQuantity}
                                onChange={(e) => setSelectedQuantity(parseInt(e.target.value) || 1)}
                                className="w-16 px-2 py-1 border rounded text-center"
                              />
                              <Button
                                onClick={() => handleSmartMatchDecision('add_quantity', match.item)}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Add {selectedQuantity}
                              </Button>
                              <Button
                                onClick={() => setShowQuantityInput(false)}
                                variant="ghost"
                                size="sm"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Scanned:</strong> {smartMatchResults.scannedText}
                </p>
                <div className="flex flex-wrap gap-1">
                  {smartMatchResults.scannedWords.map((word, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-between space-x-3">
              <Button
                onClick={() => handleSmartMatchDecision('add_new', null, {
                  name: smartMatchResults.productName || smartMatchResults.scannedWords.slice(0, 3).join(' '),
                  extracted_text: smartMatchResults.scannedText,
                  selected_words: smartMatchResults.scannedWords.slice(0, 5)
                })}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add as New Item
              </Button>
              
              <Button
                onClick={() => handleSmartMatchDecision('cancel')}
                variant="outline"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel Scan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Check Off Confirmation Popup */}
      {showCheckOffConfirmation && matchingResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {matchingResults.splitTextMode ? "Items Found - Tap to Check Off!" : "Item Found on Your List!"}
              </h3>
              <p className="text-sm text-gray-600">
                {matchingResults.splitTextMode 
                  ? `Found ${matchingResults.matches.length} matching item${matchingResults.matches.length > 1 ? 's' : ''} from your scan. Tap any item to check it off instantly.`
                  : `We found ${matchingResults.matches.length} matching item${matchingResults.matches.length > 1 ? 's' : ''} on your shopping list.`
                }
              </p>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="space-y-3">
                {matchingResults.matches.map((match, index) => (
                  <div key={match.item.id} className="border rounded-lg hover:bg-gray-50 transition-colors">
                    <div 
                      className={`p-3 cursor-pointer ${matchingResults.splitTextMode ? 'hover:bg-green-50' : ''}`}
                      onClick={() => matchingResults.splitTextMode && handleConfirmCheckOff(match.item)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {match.item.name}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <div className="flex flex-wrap gap-1">
                              {match.matching_words.map((word, wordIndex) => (
                                <span key={wordIndex} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                  {word}
                                </span>
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">
                              {Math.round(match.confidence * 100)}% match
                            </span>
                          </div>
                        </div>
                        {matchingResults.splitTextMode ? (
                          <div className="ml-3 text-green-600">
                            <Check className="w-5 h-5" />
                            <span className="text-xs block">Tap</span>
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleConfirmCheckOff(match.item)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 ml-3"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Check Off
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Scanned text:</strong> {matchingResults.scannedText}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Detected product:</strong> {matchingResults.productName}
                </p>
                {matchingResults.scannedWords && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Scanned words:</strong>
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {matchingResults.scannedWords.map((word, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {word}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-between space-x-3">
              {!matchingResults.splitTextMode && (
                <Button
                  onClick={handleDenyCheckOff}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add as New Item
                </Button>
              )}
              
              <Button
                onClick={() => {
                  setShowCheckOffConfirmation(false);
                  setMatchingResults(null);
                }}
                variant="outline"
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>

              {matchingResults.splitTextMode && (
                <Button
                  onClick={handleDenyCheckOff}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Item
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Word Selection Popup */}
      {showWordSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Words to Add</h3>
              <p className="text-sm text-gray-600">
                Choose one or more words from the detected text to combine into a shopping list item.
                {selectedWords.length > 0 && (
                  <span className="block mt-1 font-medium text-blue-600">
                    Preview: "{selectedWords.join(' ')}"
                  </span>
                )}
              </p>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {detectedWords.map((word, index) => (
                  <button
                    key={`${word}-${index}`}
                    onClick={() => toggleWordSelection(word)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      selectedWords.includes(word)
                        ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                    }`}
                  >
                    {word}
                  </button>
                ))}
              </div>
              
              {detectedWords.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No words detected in the image.</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-between space-x-3">
              <Button
                onClick={cancelWordSelection}
                variant="outline"
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              
              <Button
                onClick={confirmWordSelection}
                disabled={selectedWords.length === 0}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Add "{selectedWords.join(' ')}" {selectedWords.length > 0 ? '' : 'Selected'}
              </Button>
            </div>
            
            {/* Debug info */}
            {originalAnalysisResult?.extracted_text && (
              <details className="p-4 border-t bg-gray-50">
                <summary className="text-xs text-gray-500 cursor-pointer mb-2">
                  Raw detected text (for reference)
                </summary>
                <pre className="text-xs bg-white p-2 rounded border whitespace-pre-wrap">
                  {originalAnalysisResult.extracted_text}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Tap anywhere overlay */}
      {showTapPrompt && pendingCheckOff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 m-4 max-w-sm text-center shadow-xl">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Item Ready!</h3>
            <p className="text-gray-600 mb-4">{pendingCheckOff.message}</p>
            <p className="text-sm text-blue-600 font-medium">
              Tap anywhere on the screen to check off as placed in basket
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b w-full">
        <div className="w-full px-2 sm:px-4 lg:px-6 py-2 sm:py-4">
          <div className="flex items-center justify-between w-full overflow-hidden">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <div className="p-1 sm:p-2 bg-blue-600 rounded-lg flex-shrink-0">
                <ShoppingCart className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <h1 className="text-sm sm:text-lg lg:text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent truncate">
                    Sahani: Recipe Personaliser
                  </h1>
                  {showListNameEditor ? (
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <input
                        type="text"
                        value={currentListName}
                        onChange={(e) => setCurrentListName(e.target.value)}
                        className="px-1 sm:px-2 py-1 text-xs sm:text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-20 sm:w-32"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            saveListWithName(currentListName);
                            setShowListNameEditor(false);
                          }
                        }}
                        autoFocus
                      />
                      <Button
                        onClick={() => {
                          saveListWithName(currentListName);
                          setShowListNameEditor(false);
                        }}
                        size="sm"
                        className="px-1 sm:px-2 py-1 h-6 sm:h-8"
                      >
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={() => setShowListNameEditor(false)}
                        variant="ghost"
                        size="sm"
                        className="px-1 sm:px-2 py-1 h-6 sm:h-8"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => setShowListNameEditor(true)}
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700 px-1 sm:px-2 py-1 text-xs sm:text-sm"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      <span className="truncate max-w-16 sm:max-w-32">{currentListName}</span>
                    </Button>
                  )}
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-600 overflow-hidden">
                  <span className="truncate">Transform recipes to be healthier • Smart shopping lists • OCR powered</span>
                  {(locationMode || manualShoppingMode) && (
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <MapPin className="w-2 h-2 sm:w-3 sm:h-3" />
                      <span className={`font-medium text-xs sm:text-sm truncate ${getShoppingModeStatus().isActive ? 'text-green-600' : 'text-blue-600'}`}>
                        {isMobile ? (getShoppingModeStatus().isActive ? 'Shopping' : 'Building') : getShoppingModeStatus().message}
                      </span>
                      {manualShoppingMode && (
                        <Badge variant="default" className="bg-orange-600 text-xs px-1">
                          Manual
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Authentication Section */}
            <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0">
              {isLoadingAuth ? (
                <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              ) : isAuthenticated ? (
                <div className="flex items-center space-x-1 sm:space-x-3">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name}
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border border-gray-300"
                    />
                  ) : (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                    </div>
                  )}
                  {!isMobile && (
                    <div className="hidden sm:block min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-20 lg:max-w-32">{user?.name}</p>
                      <p className="text-xs text-gray-600 truncate max-w-20 lg:max-w-32">{user?.email}</p>
                    </div>
                  )}
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="text-gray-600 hover:text-gray-900 p-1 sm:p-2"
                  >
                    <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                    {!isMobile && <span className="hidden sm:inline ml-2">Logout</span>}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowLoginForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-2 sm:px-4 py-1 sm:py-2"
                >
                  <LogIn className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  {isMobile ? 'Login' : 'Login / Sign Up'}
                </Button>
              )}
            </div>
          </div>
          
          {/* Navigation */}
          <div className="mt-4 space-y-3">
            {/* Top Row - Main Navigation */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={currentView === 'meal-planner' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentView('meal-planner')}
                className="flex items-center space-x-1 bg-gradient-to-r from-purple-100 to-pink-100 border-purple-300"
              >
                <Calendar className="w-4 h-4" />
                <span>🍽️ Home</span>
              </Button>
              <Button
                variant={currentView === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentView('list')}
                className="flex items-center space-x-1"
              >
                <List className="w-4 h-4" />
                <span>List ({shoppingList.length})</span>
              </Button>
              
              {/* My Lists Button - Only show for authenticated users */}
              {isAuthenticated && (
                <Button
                  variant={currentView === 'my-lists' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setCurrentView('my-lists');
                    loadSavedLists();
                  }}
                  className="flex items-center space-x-1"
                >
                  <FolderOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">My Lists</span>
                  <span className="sm:hidden">Lists</span>
                </Button>
              )}
              
              {/* Stats - Mobile friendly */}
              <div className="flex items-center gap-2 ml-auto">
                <Badge variant="secondary" className="text-xs">
                  {shoppingList.length} items
                </Badge>
                <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                  {shoppingList.filter(item => item.checked_off).length} ✓
                </Badge>
              </div>
            </div>
            
            {/* Bottom Row - Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Recipe Transformer Button - PRIMARY FEATURE */}
              <Button
                onClick={() => {
                  if (!isAuthenticated) {
                    alert('Please login to use Recipe Transformer');
                    setShowLoginForm(true);
                  } else {
                    setShowRecipeSearchDialog(true);
                  }
                }}
                size="sm"
                className="flex items-center space-x-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold shadow-lg"
              >
                <span className="text-lg">🔬</span>
                <span>Recipe Transformer</span>
              </Button>
              
              {/* Meal Planner Button - Opens detailed modal */}
              <Button
                onClick={() => {
                  if (!isAuthenticated) {
                    alert('Please login to use Meal Planner');
                    setShowLoginForm(true);
                  } else {
                    setShowMealPlanner(!showMealPlanner);
                  }
                }}
                size="sm"
                variant="outline"
                className="flex items-center space-x-1"
              >
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Full Planner</span>
                <span className="sm:hidden">Plans</span>
              </Button>
              
              {/* Divider */}
              <div className="hidden sm:block w-px h-6 bg-gray-300"></div>
              <span className="hidden sm:inline text-xs text-gray-500 font-medium">Additional Services:</span>
              
              {/* Start/Stop Shopping Button */}
              {!manualShoppingMode ? (
                <Button
                  onClick={startManualShopping}
                  size="sm"
                  variant="outline"
                  className="flex items-center space-x-1"
                  disabled={shoppingList.length === 0}
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span className="hidden sm:inline">Shopping List</span>
                  <span className="sm:hidden">Shop</span>
                </Button>
              ) : (
                <Button
                  onClick={stopManualShopping}
                  size="sm"
                  variant="outline"
                  className="flex items-center space-x-1 border-orange-600 text-orange-600 hover:bg-orange-50"
                >
                  <X className="w-4 h-4" />
                  <span>Stop Shopping</span>
                </Button>
              )}
              
              {/* Import Paper List Button */}
              {!paperListMode ? (
                <Button
                  onClick={startPaperListImport}
                  size="sm"
                  variant="outline"
                  className="flex items-center space-x-1"
                >
                  <ImageIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">OCR List Import</span>
                  <span className="sm:hidden">OCR</span>
                </Button>
              ) : (
                <Button
                  onClick={stopPaperListImport}
                  size="sm"
                  variant="outline"
                  className="flex items-center space-x-1 border-purple-600 text-purple-600 hover:bg-purple-50"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel Import</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Camera View - Only show when in active scanning modes */}
        {currentView === 'camera' && (locationMode || manualShoppingMode || paperListMode) && (
          <div className="space-y-6">
            {/* Location/Shopping Mode Status */}
            {(locationMode || manualShoppingMode || paperListMode) && (
              <Card className={`border-2 ${
                paperListMode 
                  ? 'border-purple-200 bg-purple-50'
                  : getShoppingModeStatus().isActive 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-blue-200 bg-blue-50'
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {paperListMode ? (
                          <ImageIcon className="w-5 h-5 text-purple-600" />
                        ) : (
                          <MapPin className={`w-5 h-5 ${getShoppingModeStatus().isActive ? 'text-green-600' : 'text-blue-600'}`} />
                        )}
                        {manualShoppingMode && !paperListMode && (
                          <ShoppingCart className="w-5 h-5 text-orange-600" />
                        )}
                      </div>
                      <div>
                        <p className={`font-medium ${
                          paperListMode 
                            ? 'text-purple-800'
                            : getShoppingModeStatus().isActive 
                              ? 'text-green-800' 
                              : 'text-blue-800'
                        }`}>
                          {paperListMode 
                            ? 'Paper List Import Mode'
                            : getShoppingModeStatus().isActive 
                              ? 'Shopping Mode Active' 
                              : 'List Building Mode'
                          }
                          {manualShoppingMode && !paperListMode && ' (Manual)'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {paperListMode 
                            ? 'Point camera at your handwritten shopping list'
                            : getShoppingModeStatus().isActive 
                              ? 'Scan items to check them off your list'
                              : 'Scan items to add to your shopping list'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {!paperListMode && (
                        <Badge variant={getShoppingModeStatus().isActive ? 'default' : 'secondary'}>
                          {getShoppingModeStatus().confidence}% confidence
                        </Badge>
                      )}
                      {manualShoppingMode && !paperListMode && (
                        <>
                          <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                            Manual Mode
                          </Badge>
                          {renderDisplayModeButtons()}
                        </>
                      )}
                      {paperListMode && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                          OCR Mode
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Camera Section */}
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Camera className="w-5 h-5" />
                  <span>
                    {paperListMode ? 'Paper List Scanner' : 'Product Scanner'}
                  </span>
                  {getShoppingModeStatus().isActive && !paperListMode && (
                    <Badge className={`${manualShoppingMode ? 'bg-orange-600' : 'bg-green-600'}`}>
                      {manualShoppingMode ? 'Manual Shopping' : 'Shopping Mode'}
                    </Badge>
                  )}
                  {paperListMode && (
                    <Badge className="bg-purple-600">
                      Paper List Import
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="relative">
                  {cameraError ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
                      <ImageIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
                      <p className="text-red-600 mb-4 text-sm">{cameraError}</p>
                      <div className="space-y-3">
                        <Button onClick={initializeCamera} variant="outline" size="sm">
                          🔄 Try Again
                        </Button>
                        
                        {manualShoppingMode && (
                          <div className="bg-orange-50 border border-orange-200 rounded p-3 mt-4">
                            <h4 className="font-medium text-orange-800 mb-2">Shopping Mode Alternative</h4>
                            <p className="text-xs text-orange-700 mb-3">
                              Camera not available, but you can still use shopping mode by manually checking off items from your list.
                            </p>
                            <Button 
                              onClick={() => setCurrentView('list')} 
                              size="sm" 
                              className="bg-orange-600 hover:bg-orange-700 text-white"
                            >
                              📋 Go to Shopping List
                            </Button>
                          </div>
                        )}
                        
                        {paperListMode && (
                          <div className="bg-purple-50 border border-purple-200 rounded p-3 mt-4">
                            <h4 className="font-medium text-purple-800 mb-2">Paper List Import Alternative</h4>
                            <p className="text-xs text-purple-700 mb-3">
                              Camera not available. You can add items manually instead.
                            </p>
                            <Button 
                              onClick={() => {
                                setPaperListMode(false);
                                setShowAddManually(true);
                              }} 
                              size="sm" 
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              ✏️ Add Items Manually
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-64 sm:h-80 object-cover bg-gray-100"
                      />
                      <canvas ref={canvasRef} className="hidden" />
                      
                      {/* Capture Button */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                        <Button
                          onClick={paperListMode ? processPaperListImage : analyzeImage}
                          disabled={
                            (paperListMode ? isProcessingPaperList : isAnalyzing) ||
                            !videoRef.current || 
                            !streamRef.current ||
                            (videoRef.current && videoRef.current.readyState < 2)
                          }
                          size="lg"
                          className={`rounded-full w-16 h-16 p-0 ${
                            paperListMode 
                              ? 'bg-purple-600 hover:bg-purple-700' 
                              : 'bg-blue-600 hover:bg-blue-700'
                          } ${
                            (!videoRef.current || !streamRef.current || (videoRef.current && videoRef.current.readyState < 2)) 
                              ? 'opacity-50 cursor-not-allowed' 
                              : ''
                          }`}
                        >
                          {(paperListMode ? isProcessingPaperList : isAnalyzing) ? (
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            paperListMode ? <ImageIcon className="w-6 h-6" /> : <Camera className="w-6 h-6" />
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Shopping Mode Word Selection Popup */}
      {showShoppingWordSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                🛒 Select Words for Shopping List Item
              </h3>
              <p className="text-sm text-gray-600">
                This item is not on your list. Choose words from the scanned text to create a new shopping list item.
                {selectedWords.length > 0 && (
                  <span className="block mt-1 font-medium text-green-600">
                    New item: "{selectedWords.join(' ')}"
                  </span>
                )}
              </p>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {detectedWords.map((word, index) => (
                  <button
                    key={`shopping-${word}-${index}`}
                    onClick={() => toggleWordSelection(word)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      selectedWords.includes(word)
                        ? 'bg-green-600 text-white border-green-600 shadow-md'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-green-50 hover:border-green-300'
                    }`}
                  >
                    {word}
                  </button>
                ))}
              </div>
              
              {detectedWords.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No words detected in the scanned item.</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-between space-x-3">
              <Button
                onClick={cancelShoppingWordSelection}
                variant="outline"
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              
              <Button
                onClick={confirmShoppingWordSelection}
                disabled={selectedWords.length === 0}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Add and Check Off "{selectedWords.join(' ')}"
              </Button>
            </div>
            
            {/* Debug info */}
            {shoppingModeAnalysisResult?.extracted_text && (
              <details className="p-6 pt-0">
                <summary className="text-xs text-gray-500 cursor-pointer">
                  Raw detected text (for debugging)
                </summary>
                <pre className="text-xs bg-gray-100 p-2 rounded mt-1 whitespace-pre-wrap">
                  {shoppingModeAnalysisResult.extracted_text}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Paper List OCR Result */}
            {paperListResult && (
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-purple-800 mb-2">
                          Paper List Detected! ({paperListWords.length} words found)
                        </h3>
                        {paperListResult.success && paperListWords.length > 0 ? (
                          <div className="space-y-3">
                            <p className="text-sm text-purple-700 mb-3">
                              ✨ Drag and drop words to group them into items:
                            </p>
                            
                            {/* Instructions */}
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                              <p className="text-xs text-purple-700 leading-relaxed">
                                📝 <strong>Single tap</strong> to edit word text<br/>
                                🔀 <strong>Drag & drop</strong> onto another word to group them together<br/>
                                ✂️ <strong>Double-tap</strong> to separate a word into its own item<br/>
                                💾 <strong>Enter or tap outside</strong> to save edits
                              </p>
                            </div>
                            
                            {/* Grouped Words Display */}
                            <div className="space-y-3 max-h-80 overflow-y-auto">
                              {getGroupedWords().map((group, groupIndex) => (
                                <div 
                                  key={groupIndex}
                                  className="bg-white rounded-lg border-2 border-purple-200 p-3"
                                >
                                  <div className="flex items-center mb-2">
                                    <span className="text-xs font-semibold text-purple-600">
                                      Item {groupIndex + 1}
                                    </span>
                                    <Button
                                      onClick={() => {
                                        // Delete entire group
                                        const updatedWords = paperListWords.filter(
                                          w => w.groupId !== group[0].groupId
                                        );
                                        setPaperListWords(updatedWords);
                                      }}
                                      variant="ghost"
                                      size="sm"
                                      className="ml-auto text-red-600 hover:text-red-700 h-6 w-6 p-0"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                  
                                  {/* Word buttons */}
                                  <div className="flex flex-wrap gap-2">
                                    {group.map((word) => (
                                      <div key={word.id} className="relative">
                                        {editingWordIndex === word.originalIndex ? (
                                          // Edit mode - show input
                                          <input
                                            type="text"
                                            value={editingWordText}
                                            onChange={(e) => setEditingWordText(e.target.value)}
                                            onBlur={saveWordEdit}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                saveWordEdit();
                                              } else if (e.key === 'Escape') {
                                                cancelWordEdit();
                                              }
                                            }}
                                            autoFocus
                                            className="px-3 py-2 rounded-lg font-medium text-sm bg-white border-2 border-purple-500 text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-600"
                                            style={{ minWidth: '80px' }}
                                          />
                                        ) : (
                                          // View mode - draggable button
                                          <button
                                            draggable
                                            onDragStart={() => handleWordDragStart(word.originalIndex)}
                                            onDragOver={(e) => handleWordDragOver(e, word.originalIndex)}
                                            onDrop={() => handleWordDrop(word.originalIndex)}
                                            onDragEnd={handleWordDragEnd}
                                            onClick={() => handleWordClick(word.originalIndex, word.text)}
                                            onDoubleClick={() => handleWordDoubleClick(word.originalIndex)}
                                            className={`
                                              px-3 py-2 rounded-lg font-medium text-sm
                                              transition-all duration-200 cursor-move
                                              ${draggedWordIndex === word.originalIndex 
                                                ? 'bg-purple-500 text-white shadow-lg scale-105' 
                                                : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                                              }
                                              border-2 border-purple-300
                                              active:scale-95
                                            `}
                                            style={{
                                              touchAction: 'none',
                                              userSelect: 'none'
                                            }}
                                          >
                                            {word.text}
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Preview of combined item */}
                                  <div className="mt-2 pt-2 border-t border-purple-100">
                                    <p className="text-xs text-purple-600">
                                      Preview: <span className="font-semibold">{group.map(w => w.text).join(' ')}</span>
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Add separator line button */}
                            <Button
                              onClick={() => {
                                // Add a new empty group
                                const newWord = {
                                  text: 'New Item',
                                  groupId: Date.now(),
                                  id: `word-new-${Date.now()}`
                                };
                                setPaperListWords([...paperListWords, newWord]);
                              }}
                              variant="outline"
                              size="sm"
                              className="w-full border-purple-300 text-purple-700 hover:bg-purple-100"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add New Item
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm text-purple-700">
                            {paperListResult.message || 'No items detected in the image.'}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      {paperListResult.success && paperListWords.length > 0 && (
                        <Button
                          onClick={handleImportGroupedWords}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Import Items ({getGroupedWords().length})
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          setPaperListResult(null);
                          setEditablePaperListItems([]);
                          setPaperListWords([]);
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          setPaperListResult(null);
                          setEditablePaperListItems([]);
                          setPaperListWords([]);
                        }}
                        size="sm"
                        variant="ghost"
                        className="text-purple-600"
                      >
                        Scan Again
                      </Button>
                    </div>
                    
                    {/* Show raw text for debugging */}
                    {paperListResult.raw_text && (
                      <details className="mt-3">
                        <summary className="text-xs text-purple-600 cursor-pointer">
                          Raw detected text (for debugging)
                        </summary>
                        <pre className="text-xs bg-white p-2 rounded mt-1 whitespace-pre-wrap">
                          {paperListResult.raw_text}
                        </pre>
                      </details>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Analysis Result - Only shown in shopping mode now */}
            {lastAnalyzedItem && (manualShoppingMode || locationMode?.is_shopping_mode) && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-800 mb-2">Product Detected!</h3>
                      <p className="text-lg font-medium text-green-900 mb-1">
                        {lastAnalyzedItem.product_name}
                      </p>
                      {lastAnalyzedItem.extracted_text && (
                        <p className="text-sm text-green-700 opacity-75 line-clamp-2">
                          {lastAnalyzedItem.extracted_text.substring(0, 100)}...
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button
                        onClick={() => addItemToList(lastAnalyzedItem.product_name, lastAnalyzedItem.extracted_text)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                      <Button
                        onClick={() => setLastAnalyzedItem(null)}
                        size="sm"
                        variant="outline"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Manual Add Section */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Add Item Manually</h3>
                  <Button
                    onClick={() => setShowAddManually(!showAddManually)}
                    variant="outline"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                
                {showAddManually && (
                  <div className="space-y-3">
                    <div className="flex space-x-2">
                      <Input
                        placeholder="Enter item name..."
                        value={manualItemName}
                        onChange={(e) => {
                          console.log('📝 Input changed to:', e.target.value);
                          setManualItemName(e.target.value);
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddManually()}
                        className="flex-1"
                      />
                      <Button 
                        onClick={() => {
                          console.log('🔥 Add button clicked! Current item name:', manualItemName);
                          handleAddManually();
                        }} 
                        disabled={!manualItemName.trim()}
                      >
                        Add
                      </Button>
                    </div>
                    
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Default Landing View - Meal Planner */}
        {currentView === 'meal-planner' && (
          <div className="space-y-6">
            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center text-2xl">
                      <span className="text-3xl mr-3">🍽️</span>
                      Meal Planner & Recipe Transformer
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-2">
                      Plan your meals, transform recipes to be healthier, and integrate school menus
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowRecipeSearchDialog(true)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold shadow-lg"
                  >
                    🔬 Recipe Transformer
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Embed Meal Planner Content Here */}
            {isAuthenticated ? (
              <div className="bg-white rounded-xl shadow-xl">
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                      <Calendar className="w-6 h-6 mr-2 text-purple-600" />
                      Your Meal Plans
                    </h3>
                  </div>
                  
                  {/* View Selector */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant={mealPlanView === 'daily' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMealPlanView('daily')}
                    >
                      Daily
                    </Button>
                    <Button
                      variant={mealPlanView === 'weekly' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMealPlanView('weekly')}
                    >
                      1 Week
                    </Button>
                    <Button
                      variant={mealPlanView === '2weekly' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMealPlanView('2weekly')}
                    >
                      2 Weeks
                    </Button>
                    <Button
                      onClick={() => setShowRecipeSearchDialog(true)}
                      size="sm"
                      className="ml-auto bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      🔍 Search Recipes
                    </Button>
                    <Button
                      onClick={() => setShowAddMealDialog(true)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Meal
                    </Button>
                  </div>
                </div>
                
                {/* Meal Planner Content - Reuse from modal */}
                <div className="overflow-y-auto p-6" style={{maxHeight: 'calc(100vh - 400px)'}}>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>📅 Plan your meals:</strong> Use the calendar below to organize breakfasts, lunches, and dinners. 
                      Track school menus and get ingredient-based meal similarity insights!
                    </p>
                  </div>

                  {/* Rest of meal planner content will be rendered by the existing modal code */}
                  {/* For now, show a message to use the Meal Planner button */}
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-purple-300 mx-auto mb-4" />
                    <h4 className="text-xl font-semibold text-gray-700 mb-2">Start Planning Your Meals</h4>
                    <p className="text-gray-600 mb-6">Click "🍽️ Meal Planner" button above to open the full planner interface</p>
                    <Button
                      onClick={() => setShowMealPlanner(true)}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Open Full Meal Planner
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Card className="border-2 border-purple-200">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-20 h-20 text-purple-300 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Sahani!</h3>
                  <p className="text-gray-600 mb-6">
                    Sign in to access the Recipe Transformer, Meal Planner, and personalized nutrition features
                  </p>
                  <Button
                    onClick={() => setShowLoginForm(true)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-lg px-8 py-3"
                  >
                    Sign In / Sign Up
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {currentView === 'list' && (
          <div className="space-y-6">
            {/* Shopping Mode Controls */}
            <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-600 rounded-lg">
                      <ShoppingCart className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Shopping Controls</h3>
                      <p className="text-sm text-gray-600">
                        {manualShoppingMode 
                          ? 'Manual shopping mode active - scan items to check them off'
                          : 'Ready to start shopping? Use camera to check off items'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {renderDisplayModeButtons()}
                    <div className="flex space-x-2">
                      {/* Share List Button - Only show for authenticated users with a list */}
                      {listId && isAuthenticated && (
                        <Button
                          onClick={() => setShowShareDialog(true)}
                          size="sm"
                          variant="outline"
                          className="border-blue-600 text-blue-600 hover:bg-blue-50"
                        >
                          <User className="w-4 h-4 mr-2" />
                          Share List
                        </Button>
                      )}
                      
                      {!manualShoppingMode ? (
                        <Button
                          onClick={startManualShopping}
                          className="bg-green-600 hover:bg-green-700"
                          disabled={shoppingList.length === 0}
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Start Shopping
                        </Button>
                      ) : (
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => setCurrentView('camera')}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            Scan Items
                          </Button>
                          <Button
                            onClick={stopManualShopping}
                            variant="outline"
                            className="border-orange-600 text-orange-600 hover:bg-orange-50"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Stop Shopping
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Shopping List Stats */}
            {shoppingList.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Circle className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-600">{getUncheckedCount()}</p>
                    <p className="text-sm text-gray-600">Items to get</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-600">{getCheckedOffCount()}</p>
                    <p className="text-sm text-gray-600">In basket</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Shopping List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ShoppingCart className="w-5 h-5" />
                    <span>Your Shopping List</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    {renderDisplayModeButtons()}
                    <Badge variant="secondary">{shoppingList.length} items</Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {shoppingList.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Your shopping list is empty</p>
                    <p className="text-sm text-gray-500">Start scanning products to add them!</p>
                    <Button
                      onClick={() => setCurrentView('camera')}
                      className="mt-4"
                      variant="outline"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Go to Scanner
                    </Button>
                  </div>
                ) : (
                  renderItemsByDisplayMode(shoppingList)
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* My Lists View */}
        {currentView === 'my-lists' && (
          <div className="space-y-6">
            {/* Header */}
            <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-600 rounded-lg">
                      <FolderOpen className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">My Lists</h3>
                      <p className="text-sm text-gray-600">
                        Manage your saved shopping lists and shared lists
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {/* Share Current List Button */}
                    {listId && isAuthenticated && (
                      <Button
                        onClick={() => setShowShareDialog(true)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Share Current List
                      </Button>
                    )}
                    
                    {/* Auto-save Status */}
                    {lastAutoSave && (
                      <div className="text-xs text-gray-500 flex items-center space-x-1">
                        <Check className="w-3 h-3 text-green-600" />
                        <span>Auto-saved {new Date(lastAutoSave).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Loading State */}
            {isLoadingSavedLists ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading your saved lists...</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                
                {/* My Lists Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <List className="w-5 h-5" />
                      <span>My Lists ({savedLists.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {savedLists.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No saved lists yet</p>
                        <p className="text-xs">Lists are automatically saved when you make changes</p>
                      </div>
                    ) : (
                      savedLists.map((list) => (
                        <div 
                          key={list.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer group"
                          onClick={() => loadShoppingList(list.id)}
                        >
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 group-hover:text-blue-600">
                              {list.name}
                            </h4>
                            <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                              <span>{list.items_count} items</span>
                              <span>{list.checked_count} checked</span>
                              <span>{new Date(list.updated_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline" className="text-xs">
                              {list.items_count}
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                loadShoppingList(list.id);
                              }}
                            >
                              Load
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>

                {/* Shared Lists Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <User className="w-5 h-5" />
                      <span>Shared with Me ({sharedLists.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {sharedLists.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No shared lists</p>
                        <p className="text-xs">Lists shared with you will appear here</p>
                      </div>
                    ) : (
                      sharedLists.map((list) => (
                        <div 
                          key={list.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer group"
                          onClick={() => loadShoppingList(list.id)}
                        >
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 group-hover:text-blue-600">
                              {list.name}
                            </h4>
                            <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                              <span>by {list.owner}</span>
                              <span>{list.items_count} items</span>
                              <span>{list.permission}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge 
                              variant={list.permission === 'edit' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {list.permission}
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                loadShoppingList(list.id);
                              }}
                            >
                              Load
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Share Dialog */}
      {showShareDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🤝 Share "{currentListName}"
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    placeholder="Enter email address..."
                    className="w-full"
                    onKeyPress={(e) => e.key === 'Enter' && shareList()}
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permission
                  </label>
                  <select 
                    value={sharePermission}
                    onChange={(e) => setSharePermission(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="view">View Only</option>
                    <option value="edit">Can Edit</option>
                  </select>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <Button
                  onClick={shareList}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={!shareEmail.trim()}
                >
                  <User className="w-4 h-4 mr-2" />
                  Share List
                </Button>
                <Button
                  onClick={() => {
                    setShowShareDialog(false);
                    setShareEmail('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Name New Image Popup */}
      {showNameNewImagePopup && newImageData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🏷️ Name This Item
              </h3>
              
              {newImageData.imageBase64 && (
                <div className="mb-4">
                  <img 
                    src={`data:image/jpeg;base64,${newImageData.imageBase64}`}
                    alt="Scanned item" 
                    className="w-full h-32 object-cover rounded-lg bg-gray-100"
                  />
                </div>
              )}
              
              <p className="text-sm text-gray-600 mb-4">
                This appears to be a new item! Please give it a descriptive name to help future users identify it.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Item Name
                  </label>
                  <Input
                    type="text"
                    value={newImageName}
                    onChange={(e) => setNewImageName(e.target.value)}
                    placeholder="Enter a descriptive name..."
                    className="w-full"
                    onKeyPress={(e) => e.key === 'Enter' && handleNameNewImage()}
                    autoFocus
                  />
                </div>
                
                <div className="text-xs text-gray-500">
                  <p><strong>Category:</strong> {classifyItemCategory(newImageName)}</p>
                  <p><strong>Benefits:</strong> This will help other users quickly identify similar items</p>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <Button
                  onClick={handleNameNewImage}
                  disabled={!newImageName.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save & Add to List
                </Button>
                <Button
                  onClick={cancelNameNewImage}
                  variant="outline"
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Fruit Recognition Dialog */}
      {showFruitMatchDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-6 overflow-y-auto flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                {isMultiFruit ? '🥗 Fruit Bowl Scanner' : '🍎 Fruit Recognition'}
                {isMultiFruit && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {totalItemsDetected} items detected
                  </span>
                )}
              </h3>
              
              <div className="flex gap-4 mb-6">
                {/* Captured Image */}
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-gray-700 mb-2">Your Photo:</p>
                  <img 
                    src={`data:image/jpeg;base64,${fruitCapturedImage}`}
                    alt="Captured fruit"
                    className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                  />
                </div>
                
                {/* Instructions */}
                <div className="flex-1">
                  <div className="text-gray-700 mb-3">
                    {isMultiFruit ? (
                      <div>
                        <p className="font-medium text-green-700 mb-2">
                          🎉 Multiple items detected! Perfect for fruit bowl scanning.
                        </p>
                        <p>Select the items you want to add to your shopping list:</p>
                      </div>
                    ) : (
                      <p>Is this the correct fruit/vegetable?</p>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>• Use checkboxes to select items</p>
                    {isMultiFruit && <p>• Numbers like (2) show quantity detected</p>}
                    <p>• Add custom items using the text input</p>
                    <p>• Selected items will be added to your {fruitRecognitionMode === 'shopping' ? 'check-off list' : 'shopping list'}</p>
                  </div>
                </div>
              </div>
              
              {/* Fruit Matches with Checkboxes */}
              <div className="mb-6 max-h-64 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  {isMultiFruit ? 'Detected items:' : 'Possible match:'}
                </p>
                <div className="space-y-3">
                  {fruitMatches.map((match, index) => (
                    <div
                      key={index}
                      className="flex items-center p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
                    >
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mr-4">
                        <input
                          type="checkbox"
                          id={`fruit-${index}`}
                          checked={selectedFruitMatches.some(selected => selected.fruit_name === match.fruit_name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // For single fruit images, only allow one selection unless it's multi-fruit
                              if (!isMultiFruit) {
                                setSelectedFruitMatches([match]);
                              } else {
                                setSelectedFruitMatches(prev => [...prev, match]);
                              }
                            } else {
                              setSelectedFruitMatches(prev => 
                                prev.filter(selected => selected.fruit_name !== match.fruit_name)
                              );
                            }
                          }}
                          className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                      </div>
                      
                      {/* Fruit Info */}
                      <div className="flex-1">
                        <label 
                          htmlFor={`fruit-${index}`}
                          className="cursor-pointer block"
                        >
                          <h4 className="font-semibold text-gray-900 flex items-center">
                            {match.display_name || match.fruit_name}
                            {match.count > 1 && (
                              <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                {match.count} items
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {match.variant_info}
                          </p>
                          
                          {/* Confidence Bar */}
                          <div className="flex items-center mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-2 mr-3 max-w-32">
                              <div 
                                className="bg-green-500 h-2 rounded-full transition-all" 
                                style={{ width: `${match.confidence * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {Math.round(match.confidence * 100)}% confident
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Manual Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add custom item:
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={contributeFruitName}
                    onChange={(e) => setContributeFruitName(e.target.value)}
                    placeholder="e.g., Dragon Fruit, Purple Cabbage..."
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && contributeFruitName.trim() && document.getElementById('add-items-btn').click()}
                  />
                  {contributeFruitName.trim() && (
                    <Button
                      onClick={() => {
                        // Add the custom item to selected matches
                        const customMatch = {
                          fruit_name: contributeFruitName.trim(),
                          display_name: contributeFruitName.trim(),
                          count: 1,
                          confidence: 1.0,
                          match_type: 'manual',
                          variant_info: 'Manually entered'
                        };
                        setSelectedFruitMatches(prev => [...prev, customMatch]);
                        setContributeFruitName('');
                      }}
                      variant="outline"
                      size="sm"
                      className="px-4"
                    >
                      Add
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Selected Items Summary */}
              {selectedFruitMatches.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    Selected items ({selectedFruitMatches.length}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedFruitMatches.map((selected, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        {selected.display_name || selected.fruit_name}
                        <button
                          onClick={() => {
                            setSelectedFruitMatches(prev => 
                              prev.filter(s => s.fruit_name !== selected.fruit_name)
                            );
                          }}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Clear Instructions if no items selected */}
              {selectedFruitMatches.length === 0 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 flex items-center">
                    <span className="mr-2">⚠️</span>
                    <strong>Please select items above by checking the boxes next to them, or type a custom item name below.</strong>
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  id="add-items-btn"
                  onClick={async () => {
                    try {
                      if (selectedFruitMatches.length === 0) {
                        alert('Please select at least one item by checking the boxes, or add a custom item using the text input.');
                        return;
                      }

                      // Track the fruit selection action
                      trackEvent('fruit_items_selected', {
                        selected_count: selectedFruitMatches.length,
                        total_detected: fruitMatches.length,
                        is_multi_fruit: isMultiFruit,
                        mode: fruitRecognitionMode,
                        selected_items: selectedFruitMatches.map(m => m.fruit_name)
                      });

                      // Handle selected matches
                      let addedCount = 0;
                      for (const match of selectedFruitMatches) {
                        if (fruitRecognitionMode === 'shopping') {
                          // Shopping mode: check off existing items
                          const itemsToCheck = shoppingList.filter(item => 
                            item.name.toLowerCase().includes(match.fruit_name.toLowerCase()) && 
                            !item.checked_off
                          );
                          
                          // If multiple matching items and count > 1, check off up to count
                          const itemsToCheckOff = itemsToCheck.slice(0, match.count || 1);
                          for (const item of itemsToCheckOff) {
                            await checkOffItem(item.id);
                          }
                          addedCount++;
                        } else {
                          // List building mode: add new items with individual images
                          const itemName = match.display_name || match.fruit_name;
                          
                          // Get individual image from database for this fruit
                          let itemImage = null;
                          try {
                            const fruitDataResponse = await fetch(
                              `${BACKEND_URL}/api/fruit/get-image?fruit_name=${encodeURIComponent(match.fruit_name)}`,
                              {
                                headers: {
                                  ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` })
                                }
                              }
                            );
                            
                            if (fruitDataResponse.ok) {
                              const fruitData = await fruitDataResponse.json();
                              itemImage = fruitData.image_base64;
                            }
                          } catch (error) {
                            console.error('Error fetching fruit image:', error);
                          }
                          
                          // If no image found, ask user to contribute
                          if (!itemImage && isAuthenticated) {
                            console.log(`📸 No image found for ${match.fruit_name}, requesting contribution`);
                            // Store the match for later
                            setFruitNeedingImage(match);
                            setShowFruitImageContribution(true);
                            // Skip adding this item for now - will add after contribution
                            continue;
                          }
                          
                          // If count > 1, add single item with quantity note
                          if (match.count > 1) {
                            const result = await addItemToList(
                              `${match.fruit_name} (${match.count})`, 
                              null, 
                              itemImage || fruitCapturedImage
                            );
                            if (result) addedCount++;
                          } else {
                            const result = await addItemToList(itemName, null, itemImage || fruitCapturedImage);
                            if (result) addedCount++;
                          }
                        }
                      }
                      
                      // Reload the list from backend to ensure consistency
                      if (listId && addedCount > 0) {
                        console.log('✅ Reloading shopping list to show added fruits...');
                        try {
                          const listResponse = await fetch(`${BACKEND_URL}/api/shopping-list/${listId}`, {
                            headers: {
                              ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` })
                            }
                          });
                          
                          if (listResponse.ok) {
                            const listData = await listResponse.json();
                            setShoppingList(listData.items || []);
                            console.log('✅ Shopping list reloaded with added fruits');
                          }
                        } catch (error) {
                          console.error('❌ Failed to reload shopping list:', error);
                        }
                      }
                      
                      // Show success message
                      const action = fruitRecognitionMode === 'shopping' ? 'checked off' : 'added';
                      alert(`✅ Successfully ${action} ${addedCount} item${addedCount > 1 ? 's' : ''} to your list!`);
                      
                      // Close dialog and reset state
                      setShowFruitMatchDialog(false);
                      setSelectedFruitMatches([]);
                      setContributeFruitName('');
                      setFruitCapturedImage('');
                      setIsMultiFruit(false);
                      setTotalItemsDetected(0);
                      
                    } catch (error) {
                      console.error('Error handling fruit selection:', error);
                      alert('Error processing selection. Please try again.');
                    }
                  }}
                  disabled={selectedFruitMatches.length === 0}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {selectedFruitMatches.length === 0 ? (
                    <>⚠️ Select Items First</>
                  ) : fruitRecognitionMode === 'shopping' ? (
                    <>✓ Check Off Items ({selectedFruitMatches.length})</>
                  ) : (
                    <>➕ Add to List ({selectedFruitMatches.length})</>
                  )}
                </Button>
                
                <Button
                  onClick={() => {
                    setShowFruitMatchDialog(false);
                    setSelectedFruitMatches([]);
                    setContributeFruitName('');
                    setFruitCapturedImage('');
                    setIsMultiFruit(false);
                    setTotalItemsDetected(0);
                  }}
                  variant="outline"
                  className="px-8"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fruit Image Contribution Dialog */}
      {showFruitImageContribution && fruitNeedingImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                📸 Help Us Complete Our Database!
              </h3>
              
              <div className="mb-4">
                <p className="text-gray-700 mb-3">
                  We don't have an image for <strong>{fruitNeedingImage.fruit_name}</strong> yet.
                </p>
                <p className="text-sm text-gray-600 mb-3">
                  Would you like to take a photo and contribute it? You'll earn <strong className="text-green-600">10 points</strong>!
                </p>
              </div>

              {contributionImageBase64 ? (
                <div className="mb-4">
                  <img 
                    src={`data:image/jpeg;base64,${contributionImageBase64}`}
                    alt="Contribution preview"
                    className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                  />
                </div>
              ) : (
                <div className="mb-4 p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
                  <Camera className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm text-gray-500">Take a photo of {fruitNeedingImage.fruit_name}</p>
                </div>
              )}

              <div className="flex gap-3">
                {contributionImageBase64 ? (
                  <>
                    <Button
                      onClick={async () => {
                        try {
                          const response = await fetch(`${BACKEND_URL}/api/fruit/contribute-image?fruit_name=${encodeURIComponent(fruitNeedingImage.fruit_name)}&image_base64=${encodeURIComponent(contributionImageBase64)}${listId ? `&list_id=${listId}` : ''}`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                            }
                          });
                          
                          const result = await response.json();
                          
                          if (result.success) {
                            alert(`🎉 Thanks for contributing! You earned ${result.points_earned} points. Total: ${result.total_points}`);
                            
                            // Now add the item with the new image
                            const itemName = fruitNeedingImage.display_name || fruitNeedingImage.fruit_name;
                            if (fruitNeedingImage.count > 1) {
                              await addItemToList(`${fruitNeedingImage.fruit_name} (${fruitNeedingImage.count})`, null, contributionImageBase64);
                            } else {
                              await addItemToList(itemName, null, contributionImageBase64);
                            }
                            
                            // Close dialog
                            setShowFruitImageContribution(false);
                            setFruitNeedingImage(null);
                            setContributionImageBase64('');
                          }
                        } catch (error) {
                          console.error('Error contributing image:', error);
                          alert('Error contributing image. Please try again.');
                        }
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      ✅ Submit & Add to List
                    </Button>
                    <Button
                      onClick={() => setContributionImageBase64('')}
                      variant="outline"
                    >
                      Retake
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        // Open camera
                        setCurrentView('camera');
                        setShowFruitImageContribution(false);
                        // Set a flag to capture for contribution
                        window.capturingForContribution = true;
                      }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      📸 Take Photo
                    </Button>
                    <Button
                      onClick={async () => {
                        // Skip contribution, add item without image
                        const itemName = fruitNeedingImage.display_name || fruitNeedingImage.fruit_name;
                        if (fruitNeedingImage.count > 1) {
                          await addItemToList(`${fruitNeedingImage.fruit_name} (${fruitNeedingImage.count})`, null, null);
                        } else {
                          await addItemToList(itemName, null, null);
                        }
                        setShowFruitImageContribution(false);
                        setFruitNeedingImage(null);
                      }}
                      variant="outline"
                    >
                      Skip
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => {
                    setShowFruitImageContribution(false);
                    setFruitNeedingImage(null);
                    setContributionImageBase64('');
                  }}
                  variant="ghost"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contribute Fruit Dialog */}
      {showContributeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🤝 Help Improve Our Database
              </h3>
              
              {contributeFruitImage && (
                <div className="mb-4">
                  <img 
                    src={`data:image/jpeg;base64,${contributeFruitImage}`}
                    alt="Unrecognized fruit"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
              )}
              
              <div className="mb-4">
                <p className="text-gray-700 mb-4">
                  We couldn't recognize this fruit or vegetable. Help other shoppers by adding it to our database!
                </p>
                
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What fruit or vegetable is this?
                </label>
                <input
                  type="text"
                  value={contributeFruitName}
                  onChange={(e) => setContributeFruitName(e.target.value)}
                  placeholder="e.g., Dragon Fruit, Purple Cabbage..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                
                {isAuthenticated ? (
                  <p className="text-sm text-green-600 mt-2">
                    ⭐ You'll earn 10 contributor points!
                  </p>
                ) : (
                  <p className="text-sm text-orange-600 mt-2">
                    Please login to contribute and earn points
                  </p>
                )}
              </div>
              
              <div className="flex space-x-3">
                {isAuthenticated ? (
                  <Button
                    onClick={async () => {
                      if (contributeFruitName.trim()) {
                        try {
                          await contributeFruit(contributeFruitImage, contributeFruitName.trim());
                          setShowContributeDialog(false);
                          setContributeFruitName('');
                          setContributeFruitImage('');
                        } catch (error) {
                          // Error already handled in contributeFruit function
                        }
                      } else {
                        alert('Please enter a fruit or vegetable name');
                      }
                    }}
                    disabled={!contributeFruitName.trim()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    🤝 Contribute & Add
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setShowContributeDialog(false);
                      setShowLoginForm(true);
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    Login to Contribute
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setShowContributeDialog(false);
                    setContributeFruitName('');
                    setContributeFruitImage('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login/Register Form */}
      {showLoginForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
             onClick={() => setShowLoginForm(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isRegistering ? '🔐 Create Account' : '🔐 Sign In'}
              </h3>
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="Enter your email..."
                    className="w-full"
                    required
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <Input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Enter your password..."
                    className="w-full"
                    required
                  />
                </div>
                
                <div className="text-xs text-gray-500">
                  {isRegistering ? (
                    <p>Create an account to save your shopping lists and access shared items.</p>
                  ) : (
                    <p>Sign in to access your shopping lists and add items.</p>
                  )}
                </div>
                
                <div className="flex space-x-3 mt-6">
                  <Button
                    type="submit"
                    disabled={!loginEmail.trim() || !loginPassword.trim()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    {isRegistering ? 'Create Account' : 'Sign In'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowLoginForm(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
                
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsRegistering(!isRegistering);
                      setLoginEmail('');
                      setLoginPassword('');
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {isRegistering 
                      ? 'Already have an account? Sign in'
                      : "Don't have an account? Create one"
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Recipe Viewer */}
      {showRecipeViewer && currentRecipeUrl && (
        <div className="fixed inset-0 bg-white z-[80] flex flex-col">
          {/* Navigation Bar */}
          <div className="bg-gray-900 text-white p-4 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => {
                  setShowRecipeViewer(false);
                  setCurrentRecipeUrl('');
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white"
                size="sm"
              >
                ← Back to Meal
              </Button>
              <h2 className="text-lg font-semibold truncate max-w-md">
                {selectedMealDetail?.meal_name || 'Recipe'}
              </h2>
            </div>
            
            <div className="flex items-center gap-2">
              <a
                href={currentRecipeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-300 hover:text-white underline hidden sm:block"
              >
                Open in New Tab →
              </a>
              <Button
                onClick={() => {
                  setShowRecipeViewer(false);
                  setCurrentRecipeUrl('');
                  setShowMealDetailDialog(false);
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
                size="sm"
              >
                ✕ Close
              </Button>
            </div>
          </div>
          
          {/* Recipe Content */}
          <div className="flex-1 relative overflow-hidden bg-gray-50">
            <iframe
              src={currentRecipeUrl}
              className="w-full h-full border-0"
              title="Recipe"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
            {/* Helpful message overlay - shown behind iframe */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none -z-10">
              <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
                <p className="text-gray-600 mb-4">
                  ⚠️ If the recipe doesn't load, some websites block embedded viewing for security.
                </p>
                <p className="text-sm text-gray-500">
                  Click "Open in New Tab" above to view the recipe directly.
                </p>
              </div>
            </div>
          </div>
          
          {/* Bottom Action Bar */}
          <div className="bg-gray-100 p-3 flex justify-between items-center border-t">
            <Button
              onClick={() => {
                setShowRecipeViewer(false);
                setCurrentRecipeUrl('');
              }}
              variant="outline"
              size="sm"
            >
              ← Back
            </Button>
            
            <span className="text-xs text-gray-500 hidden sm:inline">
              Viewing: {new URL(currentRecipeUrl).hostname}
            </span>
            
            <Button
              onClick={() => {
                setShowRecipeViewer(false);
                setCurrentRecipeUrl('');
                setShowMealDetailDialog(false);
              }}
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Meal Detail Dialog */}
      {showMealDetailDialog && selectedMealDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedMealDetail.meal_name}</h2>
                  <div className="flex gap-2 mt-2 text-sm">
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      {selectedMealDetail.meal_type.charAt(0).toUpperCase() + selectedMealDetail.meal_type.slice(1)}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      {new Date(selectedMealDetail.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    {selectedMealDetail.is_school_menu && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        🏫 {selectedMealDetail.school_name}
                      </span>
                    )}
                    {!selectedMealDetail.is_school_menu && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                        🏠 Home
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => setShowMealDetailDialog(false)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Similarity Alert */}
              {(() => {
                const similarity = calculateMealSimilarity(selectedMealDetail, mealPlans);
                if (similarity > 0) {
                  const badge = getSimilarityBadge(similarity);
                  let alertColor = 'bg-green-50 border-green-200 text-green-800';
                  let alertTitle = 'Great Variety!';
                  let alertMessage = 'This meal has less than 25% ingredient overlap with nearby meals.';
                  
                  if (similarity > 75) {
                    alertColor = 'bg-red-50 border-red-200 text-red-800';
                    alertTitle = 'High Similarity Alert';
                    alertMessage = 'This meal shares more than 75% ingredients with nearby meals. Consider choosing something different.';
                  } else if (similarity > 25) {
                    alertColor = 'bg-amber-50 border-amber-200 text-amber-800';
                    alertTitle = 'Moderate Overlap';
                    alertMessage = 'This meal has 25-75% ingredient overlap with nearby meals.';
                  }
                  
                  return (
                    <div className={`border-2 rounded-lg p-4 mb-4 ${alertColor}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{badge}</span>
                        <strong>{alertTitle}</strong>
                      </div>
                      <p className="text-sm">{alertMessage}</p>
                      <div className="text-sm font-semibold mt-2">{similarity}% similar to meals within 48 hours</div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Ingredients */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="mr-2">📋</span>
                  Ingredients ({selectedMealDetail.ingredients?.length || 0})
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {selectedMealDetail.ingredients && selectedMealDetail.ingredients.length > 0 ? (
                    <ul className="grid grid-cols-2 gap-2">
                      {selectedMealDetail.ingredients.map((ingredient, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-purple-600 mr-2">•</span>
                          <span className="text-sm">{ingredient}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-sm">No ingredients listed</p>
                  )}
                </div>
              </div>

              {/* Cleaned Ingredients */}
              {selectedMealDetail.clean_ingredients && selectedMealDetail.clean_ingredients.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">🔍</span>
                    Key Ingredients (for similarity matching)
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedMealDetail.clean_ingredients.map((ingredient, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                        {ingredient}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="border-t pt-4 space-y-2">
                <Button
                  onClick={handleExportIngredients}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <span>🛒 Export Ingredients to Shopping List</span>
                </Button>
                
                {!selectedMealDetail.is_school_menu && (
                  <Button
                    onClick={handleDeleteMeal}
                    variant="outline"
                    className="w-full text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <span>🗑️ Delete Meal</span>
                  </Button>
                )}
              </div>

              {/* Recipe Link */}
              {selectedMealDetail.recipe_url && (
                <div className="border-t pt-4 space-y-2">
                  <a
                    href={selectedMealDetail.recipe_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <span>📖 View Full Recipe (Opens in New Tab)</span>
                    </Button>
                  </a>
                  <Button
                    onClick={() => {
                      setCurrentRecipeUrl(selectedMealDetail.recipe_url);
                      setShowRecipeViewer(true);
                    }}
                    variant="outline"
                    className="w-full text-gray-600 border-gray-300"
                  >
                    <span>📱 Try Embedded View</span>
                  </Button>
                </div>
              )}

              {/* Close Button */}
              <div className="mt-6 pt-4 border-t">
                <Button
                  onClick={() => setShowMealDetailDialog(false)}
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Ingredients Dialog */}
      {showExportDialog && selectedMealDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[75] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Export Ingredients to Shopping List
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Export {selectedMealDetail.ingredients?.length || 0} ingredient{(selectedMealDetail.ingredients?.length || 0) !== 1 ? 's' : ''} from <span className="font-semibold">{selectedMealDetail.meal_name}</span>
                </p>
                
                {/* List Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Shopping List
                  </label>
                  <select
                    value={selectedExportListId}
                    onChange={(e) => setSelectedExportListId(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="new">➕ Create New List</option>
                    {userShoppingLists.map(list => (
                      <option key={list.id} value={list.id}>
                        {list.name} ({list.items_count} items)
                      </option>
                    ))}
                  </select>
                </div>

                {/* New List Name Input */}
                {selectedExportListId === 'new' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New List Name
                    </label>
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="e.g., Weekly Groceries"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              {/* Ingredient Preview */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                <p className="text-xs font-semibold text-gray-600 mb-2">Ingredients to export:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  {(selectedMealDetail.ingredients || selectedMealDetail.clean_ingredients || []).slice(0, 5).map((ingredient, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span>{ingredient}</span>
                    </li>
                  ))}
                  {(selectedMealDetail.ingredients?.length || 0) > 5 && (
                    <li className="text-gray-500 italic">
                      ...and {(selectedMealDetail.ingredients?.length || 0) - 5} more
                    </li>
                  )}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowExportDialog(false);
                    setSelectedExportListId('new');
                    setNewListName('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmExportIngredients}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Export
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location-Based School Picker Dialog */}
      {showLocationPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[75] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Find Schools Nearby</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Found {nearbySchools.length} schools within {searchRadius / 1000}km
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setShowLocationPicker(false);
                    setSelectedSchoolsForImport([]);
                    setNearbySchools([]);
                  }}
                  variant="ghost"
                  size="sm"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Search Box */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search by name, town, or postcode
                </label>
                <input
                  type="text"
                  placeholder="Type to search schools..."
                  onChange={(e) => searchSchoolsByText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* View Mode Toggle */}
              <div className="flex gap-2">
                <Button
                  variant={locationViewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLocationViewMode('list')}
                >
                  📋 List View
                </Button>
                <Button
                  variant={locationViewMode === 'map' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLocationViewMode('map')}
                >
                  🗺️ Map View
                </Button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6">
              {locationViewMode === 'list' ? (
                <div className="space-y-3">
                  {nearbySchools.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No schools found.</p>
                      <p className="text-sm mt-2">Try searching by name or postcode.</p>
                    </div>
                  ) : (
                    nearbySchools.map((school) => {
                      const isSelected = selectedSchoolsForImport.some(s => s.id === school.id);
                      
                      return (
                        <div
                          key={school.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            isSelected ? 'bg-blue-50 border-blue-500' : 'bg-white hover:bg-gray-50'
                          }`}
                          onClick={() => toggleSchoolSelection(school)}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSchoolSelection(school)}
                              className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{school.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                📍 {school.town}, {school.county}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Postcode: {school.postcode}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="mb-4 text-center">
                    <p className="text-gray-600 font-medium mb-2">🗺️ Map View - Schools by Location</p>
                    <p className="text-sm text-gray-500">
                      Select schools using checkboxes below
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                    {nearbySchools.length === 0 ? (
                      <div className="col-span-2 text-center py-8 text-gray-500">
                        <p>No schools found.</p>
                      </div>
                    ) : (
                      nearbySchools.map((school) => {
                        const isSelected = selectedSchoolsForImport.some(s => s.id === school.id);
                        return (
                          <div
                            key={school.id}
                            className={`border rounded-lg p-3 cursor-pointer transition-all ${
                              isSelected ? 'bg-blue-50 border-blue-500' : 'bg-white hover:bg-gray-50'
                            }`}
                            onClick={() => toggleSchoolSelection(school)}
                          >
                            <div className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSchoolSelection(school)}
                                className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex-1">
                                <div className="flex items-start gap-1">
                                  <span className="text-lg">📍</span>
                                  <div>
                                    <h5 className="font-semibold text-sm text-gray-900">{school.name}</h5>
                                    <p className="text-xs text-gray-600 mt-0.5">{school.town}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{school.postcode}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {selectedSchoolsForImport.length} school{selectedSchoolsForImport.length !== 1 ? 's' : ''} selected
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowLocationPicker(false);
                      setSelectedSchoolsForImport([]);
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={importSelectedSchools}
                    disabled={selectedSchoolsForImport.length === 0}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    🍽️ Import School Dinner Menu{selectedSchoolsForImport.length > 1 ? 's' : ''}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* School Manager Dialog */}
      {showSchoolManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Manage Schools</h3>
              
              <div className="space-y-2 mb-4">
                {trackedSchools.map((school, idx) => {
                  const schoolName = typeof school === 'string' ? school : school.name;
                  const schoolInfo = typeof school === 'string' ? '' : `${school.town || ''}, ${school.postcode || ''}`;
                  
                  return (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="font-medium">{schoolName}</div>
                        {schoolInfo && <div className="text-xs text-gray-500">{schoolInfo}</div>}
                      </div>
                      <Button
                        onClick={() => {
                          const updated = trackedSchools.filter((_, i) => i !== idx);
                          setTrackedSchools(updated);
                          localStorage.setItem('trackedSchools', JSON.stringify(updated));
                        }}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowSchoolManager(false);
                    setShowLocationPicker(true);
                    searchSchoolsByText(''); // Load all schools
                  }}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add School
                </Button>
                <Button
                  onClick={() => {
                    // Save changes to localStorage
                    localStorage.setItem('trackedSchools', JSON.stringify(trackedSchools));
                    setShowSchoolManager(false);
                  }}
                  size="sm"
                  className="flex-1"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meal Planner Modal */}
      {showMealPlanner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Calendar className="w-6 h-6 mr-2 text-purple-600" />
                  Meal Planner & School Menu Tracker
                </h2>
                <Button
                  onClick={() => setShowMealPlanner(false)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* View Selector */}
              <div className="flex gap-2">
                <Button
                  variant={mealPlanView === 'daily' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMealPlanView('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={mealPlanView === 'weekly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMealPlanView('weekly')}
                >
                  1 Week
                </Button>
                <Button
                  variant={mealPlanView === '2weekly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMealPlanView('2weekly')}
                >
                  2 Weeks
                </Button>
                <Button
                  onClick={() => setShowRecipeSearchDialog(true)}
                  size="sm"
                  className="ml-auto bg-purple-600 hover:bg-purple-700 text-white"
                >
                  🔍 Search Recipes
                </Button>
                <Button
                  onClick={() => setShowAddMealDialog(true)}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Meal
                </Button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>🎯 Goal:</strong> Track school menus and plan home meals to ensure variety. 
                  <span className="ml-2">🟢 &lt;25% similar = Great variety</span>
                  <span className="ml-2">🟠 25-75% = Moderate</span>
                  <span className="ml-2">🔴 &gt;75% = Too similar</span>
                </p>
              </div>
              
              {/* Date Navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  onClick={() => {
                    if (mealPlanView === 'daily') goToPreviousDay();
                    else goToPreviousWeek();
                  }}
                  size="sm"
                  variant="outline"
                >
                  ← {mealPlanView === 'daily' ? 'Previous Day' : 'Previous Week'}
                </Button>
                
                <div className="text-center">
                  <div className="font-semibold text-gray-900">
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  <Button
                    onClick={() => setSelectedDate(new Date())}
                    size="sm"
                    variant="ghost"
                    className="text-xs text-purple-600"
                  >
                    Today
                  </Button>
                </div>
                
                <Button
                  onClick={() => {
                    if (mealPlanView === 'daily') goToNextDay();
                    else goToNextWeek();
                  }}
                  size="sm"
                  variant="outline"
                >
                  {mealPlanView === 'daily' ? 'Next Day' : 'Next Week'} →
                </Button>
              </div>

              {/* Daily View Controls */}
              {mealPlanView === 'daily' && (
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={dailyViewLayout === 'columns' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDailyViewLayout('columns')}
                  >
                    📊 Columns
                  </Button>
                  <Button
                    variant={dailyViewLayout === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDailyViewLayout('list')}
                  >
                    📋 List
                  </Button>
                  
                  {/* School Setup Section */}
                  {!hasSetupSchools && isAuthenticated && (
                    <div className="flex items-center gap-2 flex-1 mx-3 relative">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={schoolInputValue}
                          onChange={(e) => {
                            setSchoolInputValue(e.target.value);
                            searchSchoolsByText(e.target.value);
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && handleSchoolSetup()}
                          placeholder="Start typing school name or postcode..."
                          className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {nearbySchools.length > 0 && schoolInputValue.length >= 2 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {nearbySchools.slice(0, 10).map((school) => (
                              <div
                                key={school.id}
                                onClick={async () => {
                                  // Add school directly from database
                                  const newSchool = {
                                    name: school.name,
                                    town: school.town,
                                    postcode: school.postcode,
                                    id: school.id,
                                    color: '#' + Math.floor(Math.random()*16777215).toString(16)
                                  };
                                  
                                  setTrackedSchools([...trackedSchools, newSchool]);
                                  localStorage.setItem('trackedSchools', JSON.stringify([...trackedSchools, newSchool]));
                                  localStorage.setItem('hasSetupSchools', 'true');
                                  setHasSetupSchools(true);
                                  
                                  // Load menus
                                  loadSchoolMenus(school.id);
                                  
                                  // Clear input
                                  setSchoolInputValue('');
                                  setNearbySchools([]);
                                  
                                  console.log(`✅ Added ${school.name} from database`);
                                }}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                              >
                                <div className="font-medium text-sm">{school.name}</div>
                                <div className="text-xs text-gray-500">{school.town}, {school.postcode}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={handleSchoolSetup}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                      >
                        ➕ Add Schools
                      </Button>
                      <Button
                        onClick={() => {
                          searchSchoolsByText('');
                          setShowLocationPicker(true);
                        }}
                        size="sm"
                        variant="outline"
                        className="whitespace-nowrap"
                      >
                        📍 Browse All
                      </Button>
                    </div>
                  )}
                  
                  {hasSetupSchools && (
                    <Button
                      onClick={() => setShowSchoolSetup(!showSchoolSetup)}
                      size="sm"
                      variant="outline"
                      className="mx-2"
                    >
                      {showSchoolSetup ? '✓ Done' : '➕ Add More Schools'}
                    </Button>
                  )}
                  
                  {/* Expandable school input for existing users */}
                  {showSchoolSetup && hasSetupSchools && (
                    <div className="flex items-center gap-2 flex-1 mx-3 relative">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={schoolInputValue}
                          onChange={(e) => {
                            setSchoolInputValue(e.target.value);
                            searchSchoolsByText(e.target.value);
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && handleSchoolSetup()}
                          placeholder="Start typing school name or postcode..."
                          className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {nearbySchools.length > 0 && schoolInputValue.length >= 2 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {nearbySchools.slice(0, 10).map((school) => (
                              <div
                                key={school.id}
                                onClick={async () => {
                                  // Add school directly from database
                                  const newSchool = {
                                    name: school.name,
                                    town: school.town,
                                    postcode: school.postcode,
                                    id: school.id,
                                    color: '#' + Math.floor(Math.random()*16777215).toString(16)
                                  };
                                  
                                  setTrackedSchools([...trackedSchools, newSchool]);
                                  localStorage.setItem('trackedSchools', JSON.stringify([...trackedSchools, newSchool]));
                                  localStorage.setItem('hasSetupSchools', 'true');
                                  setHasSetupSchools(true);
                                  
                                  // Load menus
                                  loadSchoolMenus(school.id);
                                  
                                  // Clear input
                                  setSchoolInputValue('');
                                  setNearbySchools([]);
                                  
                                  console.log(`✅ Added ${school.name} from database`);
                                }}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                              >
                                <div className="font-medium text-sm">{school.name}</div>
                                <div className="text-xs text-gray-500">{school.town}, {school.postcode}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={handleSchoolSetup}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                      >
                        ➕ Add
                      </Button>
                      <Button
                        onClick={() => {
                          searchSchoolsByText('');
                          setShowLocationPicker(true);
                        }}
                        size="sm"
                        variant="outline"
                        className="whitespace-nowrap"
                      >
                        📍 Browse All
                      </Button>
                    </div>
                  )}
                  
                  <Button
                    onClick={() => setShowSchoolManager(true)}
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                  >
                    🏫 Manage Schools ({trackedSchools.length})
                  </Button>
                </div>
              )}
              
              {/* Calendar Views */}
              {mealPlanView === 'daily' && dailyViewLayout === 'columns' && (
                <div className="grid gap-4" style={{gridTemplateColumns: `repeat(${trackedSchools.length + 1}, minmax(0, 1fr))`}}>
                  {/* Home Meals Column */}
                  <div className="border rounded-lg">
                    <div className="bg-green-50 p-3 border-b">
                      <h3 className="font-semibold text-green-800">🏠 Home Meals</h3>
                    </div>
                    <div className="p-3 space-y-2">
                      {['breakfast', 'lunch', 'dinner'].map(mealType => {
                        const meals = getMealsByTypeAndSchool(selectedDate, mealType, false);
                        return (
                          <div key={mealType} className="border rounded p-2 bg-white">
                            <div className="text-xs font-medium text-gray-600 uppercase mb-1">{mealType}</div>
                            {meals.length > 0 ? meals.map(meal => {
                              const similarity = calculateMealSimilarity(meal, mealPlans);
                              const colorClass = getSimilarityColor(similarity);
                              const badge = getSimilarityBadge(similarity);
                              
                              return (
                                <button
                                  key={meal.id}
                                  onClick={() => {
                                    setSelectedMealDetail(meal);
                                    setShowMealDetailDialog(true);
                                  }}
                                  className={`w-full text-left text-sm p-2 rounded border-2 hover:shadow-md transition-all ${colorClass}`}
                                >
                                  <div className="flex justify-between items-start">
                                    <span className="font-medium">{meal.meal_name}</span>
                                    {similarity > 0 && <span className="text-xs">{badge}</span>}
                                  </div>
                                  {similarity > 0 && (
                                    <div className="text-xs mt-1 opacity-75">{similarity}% similar</div>
                                  )}
                                </button>
                              );
                            }) : (
                              <div className="text-xs text-gray-400">No meal</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* School Columns */}
                  {trackedSchools.map((school, idx) => {
                    const schoolMenuData = schoolMenuOptions[school.id] || { menus: [], loaded: false };
                    
                    return (
                      <div key={idx} className="border rounded-lg">
                        <div className="bg-blue-50 p-3 border-b">
                          <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-blue-800 truncate flex-1">🏫 {typeof school === 'string' ? school : school.name}</h3>
                            {school.id && schoolMenuData.loaded && schoolMenuData.menus.length > 0 && (
                              <button
                                onClick={() => importSchoolMenusToPlanner(school.id, school.name)}
                                className="ml-2 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded whitespace-nowrap"
                                title="Import menus for 2 weeks"
                              >
                                📥 Import 2 Weeks
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 p-3">
                          {/* Choice Column */}
                          <div className="border-r pr-2">
                            <div className="text-xs font-bold text-green-700 uppercase mb-2 text-center">✓ Choice</div>
                            <div className="space-y-2">
                              {['lunch'].map(mealType => {
                                const meals = getMealsByTypeAndSchool(selectedDate, mealType, true, typeof school === 'string' ? school : school.name);
                                return (
                                  <div 
                                    key={mealType} 
                                    className="border rounded p-2 bg-green-50 min-h-[100px]"
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDropToChoice(e, school.id, mealType)}
                                  >
                                    <div className="text-xs font-medium text-gray-600 uppercase mb-1">{mealType}</div>
                                    {meals.length > 0 ? meals.map(meal => {
                                      const similarity = calculateMealSimilarity(meal, mealPlans);
                                      const colorClass = getSimilarityColor(similarity);
                                      const badge = getSimilarityBadge(similarity);
                                      
                                      return (
                                        <button
                                          key={meal.id}
                                          draggable
                                          onDragStart={(e) => handleDragStart(e, { ...meal, meal_id: meal.id }, school.id)}
                                          onClick={() => {
                                            setSelectedMealDetail(meal);
                                            setShowMealDetailDialog(true);
                                          }}
                                          className={`w-full text-left text-xs p-1.5 rounded border-2 hover:shadow-md transition-all cursor-move ${colorClass}`}
                                          title="Click to view details or drag to remove"
                                        >
                                          <div className="flex justify-between items-start">
                                            <span className="font-medium">{meal.meal_name}</span>
                                            {similarity > 0 && <span className="text-xs">{badge}</span>}
                                          </div>
                                        </button>
                                      );
                                    }) : (
                                      <div className="text-xs text-gray-400 italic text-center py-4">Drag and drop choice from options Here</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          
                          {/* Options Column */}
                          <div 
                            className="pl-2"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDropToOptions(e, school.id)}
                          >
                            <div className="text-xs font-bold text-blue-700 uppercase mb-2 text-center">📋 Options</div>
                            <div className="space-y-2">
                              {['lunch'].map(mealType => {
                                // Get current day of week
                                const currentDay = selectedDate.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
                                
                                // Get menu items for TODAY from standardized menus
                                let todaysMenuItems = [];
                                
                                // Check if we have standardized menus
                                if (schoolMenuData.standardized_menus && schoolMenuData.standardized_menus.length > 0) {
                                  // Find the menu for current week
                                  const currentDateStr = selectedDate.toISOString().split('T')[0];
                                  
                                  // Find menu where selected date is within the week
                                  const currentMenu = schoolMenuData.standardized_menus.find(menu => {
                                    const weekStart = new Date(menu.week_start_date);
                                    const weekEnd = new Date(weekStart);
                                    weekEnd.setDate(weekEnd.getDate() + 4); // Friday
                                    
                                    const selectedDateObj = new Date(currentDateStr);
                                    return selectedDateObj >= weekStart && selectedDateObj <= weekEnd;
                                  });
                                  
                                  if (currentMenu && currentMenu.structured_menu && currentMenu.structured_menu.days) {
                                    todaysMenuItems = currentMenu.structured_menu.days[currentDay] || [];
                                  }
                                  
                                  // If no items for today, show all items from current week
                                  if (todaysMenuItems.length === 0 && currentMenu) {
                                    todaysMenuItems = currentMenu.all_menu_items || [];
                                  }
                                } else {
                                  // Fallback to legacy menu format
                                  todaysMenuItems = schoolMenuData.menus
                                    .flatMap(menu => menu.menu_items || [])
                                    .filter(item => item && item.length > 0)
                                    .slice(0, 10);
                                }
                                
                                return (
                                  <div key={mealType} className="border rounded p-2 bg-blue-50 min-h-[100px]">
                                    <div className="text-xs font-medium text-gray-600 uppercase mb-1">
                                      {currentDay.charAt(0).toUpperCase() + currentDay.slice(1)} {mealType}
                                    </div>
                                    {schoolMenuData.loaded ? (
                                      todaysMenuItems.length > 0 ? (
                                        <div className="space-y-1 max-h-60 overflow-y-auto">
                                          {todaysMenuItems.map((item, itemIdx) => {
                                            // Calculate similarity for this menu option
                                            const tempMeal = {
                                              meal_name: item,
                                              ingredients: item.split(' ').filter(w => w.length > 3),
                                              clean_ingredients: item.split(' ').filter(w => w.length > 3)
                                            };
                                            const similarity = calculateMealSimilarity(tempMeal, mealPlans);
                                            const colorClass = getSimilarityColor(similarity);
                                            const badge = getSimilarityBadge(similarity);
                                            
                                            return (
                                              <button
                                                key={itemIdx}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, { meal_name: item, ingredients: [] }, school.id)}
                                                className={`w-full text-left text-xs p-1.5 rounded border-2 hover:shadow-md transition-all cursor-move ${colorClass}`}
                                                title="Drag to Choice column"
                                              >
                                                <div className="flex justify-between items-start">
                                                  <span className="font-medium">{item}</span>
                                                  {similarity > 0 && <span className="text-xs">{badge}</span>}
                                                </div>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <div className="text-xs text-gray-400 italic">No menu for {currentDay}</div>
                                      )
                                    ) : (
                                      <div className="text-xs text-gray-400 italic">Loading...</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {mealPlanView === 'daily' && dailyViewLayout === 'list' && (
                <div className="space-y-4">
                  {['breakfast', 'lunch', 'dinner'].map(mealType => (
                    <div key={mealType} className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-800 uppercase text-sm mb-3">{mealType}</h3>
                      <div className="space-y-3">
                        <div>
                          <div className="text-xs font-medium text-green-700 mb-1">🏠 Home</div>
                          {getMealsByTypeAndSchool(selectedDate, mealType, false).map(meal => {
                            const similarity = calculateMealSimilarity(meal, mealPlans);
                            const colorClass = getSimilarityColor(similarity);
                            const badge = getSimilarityBadge(similarity);
                            
                            return (
                              <button
                                key={meal.id}
                                onClick={() => {
                                  setSelectedMealDetail(meal);
                                  setShowMealDetailDialog(true);
                                }}
                                className={`w-full text-left p-2 rounded text-sm mb-1 border-2 hover:shadow-md transition-all ${colorClass}`}
                              >
                                <div className="flex justify-between">
                                  <span>{meal.meal_name}</span>
                                  {similarity > 0 && <span>{badge} {similarity}%</span>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {trackedSchools.map((school, idx) => (
                          <div key={idx}>
                            <div className="text-xs font-medium text-blue-700 mb-1">🏫 {school}</div>
                            {getMealsByTypeAndSchool(selectedDate, mealType, true, school).map(meal => {
                              const similarity = calculateMealSimilarity(meal, mealPlans);
                              const colorClass = getSimilarityColor(similarity);
                              const badge = getSimilarityBadge(similarity);
                              
                              return (
                                <button
                                  key={meal.id}
                                  onClick={() => {
                                    setSelectedMealDetail(meal);
                                    setShowMealDetailDialog(true);
                                  }}
                                  className={`w-full text-left p-2 rounded text-sm mb-1 border-2 hover:shadow-md transition-all ${colorClass}`}
                                >
                                  <div className="flex justify-between">
                                    <span>{meal.meal_name}</span>
                                    {similarity > 0 && <span>{badge} {similarity}%</span>}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(mealPlanView === 'weekly' || mealPlanView === '2weekly') && (
                <div className="space-y-2">
                  {Array.from({ length: mealPlanView === 'weekly' ? 7 : 14 }, (_, i) => {
                    const date = new Date(selectedDate);
                    date.setDate(date.getDate() + i);
                    const meals = getMealsForDate(date);
                    
                    return (
                      <div key={i} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-medium">
                            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </div>
                          <div className="text-xs text-gray-500">{meals.length} meals</div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {['breakfast', 'lunch', 'dinner'].map(type => {
                            const typeMeals = meals.filter(m => m.meal_type === type);
                            return (
                              <div key={type}>
                                <div className="font-medium text-gray-600 uppercase mb-1">{type.slice(0,3)}</div>
                                {typeMeals.slice(0, 2).map(meal => (
                                  <div key={meal.id} className="truncate">{meal.meal_name}</div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Add Meal Dialog */}
      {showAddMealDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Add Meal Plan</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <input
                    type="date"
                    value={newMealData.date}
                    onChange={(e) => setNewMealData({...newMealData, date: e.target.value})}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={!newMealData.isSchoolMenu ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewMealData({...newMealData, isSchoolMenu: false})}
                    >
                      🏠 Home Meal
                    </Button>
                    <Button
                      type="button"
                      variant={newMealData.isSchoolMenu ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewMealData({...newMealData, isSchoolMenu: true})}
                    >
                      🏫 School Menu
                    </Button>
                  </div>
                </div>
                
                {newMealData.isSchoolMenu && (
                  <div>
                    <label className="block text-sm font-medium mb-1">School Name *</label>
                    <input
                      type="text"
                      value={newMealData.schoolName}
                      onChange={(e) => setNewMealData({...newMealData, schoolName: e.target.value})}
                      placeholder="e.g., Oak Elementary"
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-1">Meal Time</label>
                  <select
                    value={newMealData.mealType}
                    onChange={(e) => setNewMealData({...newMealData, mealType: e.target.value})}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                    <option value="snack">Snack</option>
                  </select>
                </div>
                
                <div className="relative">
                  <label className="block text-sm font-medium mb-1">
                    Meal Name * 
                    <span className="text-xs text-gray-500 ml-2">(Search from {availableRecipes.length} recipes)</span>
                  </label>
                  <input
                    type="text"
                    value={newMealData.mealName}
                    onChange={(e) => handleRecipeSearch(e.target.value)}
                    onFocus={() => {
                      if (availableRecipes.length === 0) loadRecipes();
                    }}
                    placeholder="Start typing to search recipes..."
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                  />
                  
                  {/* Recipe Dropdown */}
                  {showRecipeDropdown && filteredRecipes.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredRecipes.slice(0, 10).map((recipe, idx) => (
                        <div
                          key={idx}
                          onClick={() => selectRecipe(recipe)}
                          className="p-2 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                        >
                          <div className="font-medium text-sm">{recipe.title}</div>
                          <div className="text-xs text-gray-500">{recipe.ingredients.length} ingredients • {recipe.category}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Ingredients * (one per line)
                    <span className="text-xs text-gray-500 ml-2">Auto-filled from recipe</span>
                  </label>
                  <textarea
                    value={newMealData.ingredients}
                    onChange={(e) => setNewMealData({...newMealData, ingredients: e.target.value})}
                    placeholder="Select a recipe above or type ingredients manually"
                    rows="6"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  <div className="flex justify-between items-center text-xs text-gray-500 mt-1">
                    <span>{newMealData.ingredients.split('\n').filter(i => i.trim()).length} ingredients</span>
                    {newMealData.recipeUrl && (
                      <a 
                        href={newMealData.recipeUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline flex items-center"
                      >
                        View Full Recipe →
                      </a>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={async () => {
                    try {
                      // Validate required fields
                      if (!newMealData.date) {
                        alert('⚠️ Please select a date');
                        return;
                      }
                      if (!newMealData.mealName.trim()) {
                        alert('⚠️ Please enter a meal name');
                        return;
                      }
                      if (!newMealData.ingredients.trim()) {
                        alert('⚠️ Please add ingredients');
                        return;
                      }
                      if (newMealData.isSchoolMenu && !newMealData.schoolName.trim()) {
                        alert('⚠️ Please enter school name');
                        return;
                      }
                      
                      const ingredientsList = newMealData.ingredients.split('\n').filter(i => i.trim());
                      
                      console.log('💾 Saving meal plan:', {
                        date: newMealData.date,
                        meal_type: newMealData.mealType,
                        meal_name: newMealData.mealName,
                        ingredients_count: ingredientsList.length,
                        is_school_menu: newMealData.isSchoolMenu
                      });
                      
                      const requestBody = {
                        date: newMealData.date,
                        meal_type: newMealData.mealType,
                        meal_name: newMealData.mealName,
                        ingredients: ingredientsList,
                        is_school_menu: newMealData.isSchoolMenu,
                        school_name: newMealData.schoolName || null,
                        recipe_url: newMealData.recipeUrl || null
                      };
                      
                      console.log('📤 Request body:', requestBody);
                      
                      const response = await fetch(`${BACKEND_URL}/api/meal-plan/save`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                        },
                        body: JSON.stringify(requestBody)
                      });
                      
                      const result = await response.json();
                      console.log('📊 Save response:', result);
                      
                      if (response.ok && result.success) {
                        alert(`✅ Meal plan saved!\n\n${newMealData.mealName}\n${newMealData.date} - ${newMealData.mealType}\n${ingredientsList.length} ingredients`);
                        
                        // Reload meal plans to show the new one
                        const startDate = new Date(selectedDate);
                        const endDate = new Date(selectedDate);
                        if (mealPlanView === 'weekly') endDate.setDate(endDate.getDate() + 6);
                        else if (mealPlanView === '2weekly') endDate.setDate(endDate.getDate() + 13);
                        loadMealPlans(startDate, endDate);
                        
                        // Reset form
                        setShowAddMealDialog(false);
                        setNewMealData({
                          date: '',
                          mealType: 'breakfast',
                          mealName: '',
                          ingredients: '',
                          isSchoolMenu: false,
                          schoolName: '',
                          recipeUrl: ''
                        });
                        setRecipeSearchQuery('');
                        setShowRecipeDropdown(false);
                      } else {
                        alert(`❌ Error: ${result.detail || 'Failed to save meal plan'}`);
                      }
                    } catch (error) {
                      console.error('❌ Error saving meal plan:', error);
                      alert(`❌ Error saving meal plan: ${error.message}`);
                    }
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  disabled={!newMealData.date || !newMealData.mealName || !newMealData.ingredients}
                >
                  💾 Save Meal
                </Button>
                <Button
                  onClick={() => {
                    setShowAddMealDialog(false);
                    setNewMealData({
                      date: '',
                      mealType: 'breakfast',
                      mealName: '',
                      ingredients: '',
                      isSchoolMenu: false,
                      schoolName: ''
                    });
                    setRecipeSearchQuery('');
                    setShowRecipeDropdown(false);
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Search Dialog */}
      {showRecipeSearchDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">🔬 Recipe Transformer</h3>
                  <p className="text-sm text-gray-600 mt-1">Search, analyze, and optimize recipes for better health</p>
                </div>
                <button
                  onClick={() => {
                    setShowRecipeSearchDialog(false);
                    setRecipeTransformerQuery('');
                    setRecipeSearchResults([]);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Search Input */}
              <div className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={recipeTransformerQuery}
                    onChange={(e) => setRecipeTransformerQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleTransformerRecipeSearch()}
                    placeholder="Search for a recipe (e.g., macaroni cheese)..."
                    className="flex-1 p-3 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <Button
                    onClick={handleTransformerRecipeSearch}
                    disabled={isLoadingRecipes}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                  >
                    {isLoadingRecipes ? '⏳' : '🔍'} Search
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Search returns top 5 recipes ranked by reviews with full health analysis
                </p>
              </div>

              {/* Recipe Results */}
              {recipeSearchResults.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg mb-3">
                    Found {recipeSearchResults.length} recipes for "{recipeTransformerQuery}"
                  </h4>
                  
                  {recipeSearchResults.map((recipe, idx) => (
                    <div
                      key={idx}
                      className="border-2 border-purple-200 rounded-lg p-4 hover:border-purple-400 transition cursor-pointer"
                      onClick={() => handleSelectRecipe(recipe)}
                    >
                      <div className="flex gap-4">
                        {/* Recipe Image */}
                        <img
                          src={recipe.image}
                          alt={recipe.name}
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                        
                        {/* Recipe Info */}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h5 className="font-bold text-lg">{recipe.name}</h5>
                              <p className="text-sm text-gray-600">{recipe.source}</p>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-1">
                                <span className="text-yellow-500">⭐</span>
                                <span className="font-semibold">{recipe.rating}</span>
                              </div>
                              <p className="text-xs text-gray-500">{recipe.reviews} reviews</p>
                            </div>
                          </div>
                          
                          <div className="flex gap-4 mt-2 text-sm text-gray-600">
                            <span>🍽️ {recipe.yield} servings</span>
                            <span>⏱️ {recipe.totalTime} min</span>
                            <span>🔥 {recipe.calories} cal</span>
                          </div>

                          {/* Health Scores */}
                          {recipe.health_scores && (
                            <div className="mt-3 flex gap-2 flex-wrap">
                              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                recipe.health_scores.npm_color === 'green' ? 'bg-green-100 text-green-800' :
                                recipe.health_scores.npm_color === 'amber' ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                NPM: {recipe.health_scores.npm_label} ({recipe.health_scores.npm_score})
                              </div>
                              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                recipe.health_scores.nova_score <= 2 ? 'bg-green-100 text-green-800' :
                                recipe.health_scores.nova_score === 3 ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                NOVA {recipe.health_scores.nova_score}: {recipe.health_scores.nova_label}
                              </div>
                              
                              {/* HFSS Indicators */}
                              <div className="flex gap-1 items-center">
                                <span className="text-xs text-gray-600">Fat:</span>
                                <div className={`w-4 h-4 rounded-full ${getHFSSColor(recipe.health_scores.hfss_colors.fat)}`}></div>
                              </div>
                              <div className="flex gap-1 items-center">
                                <span className="text-xs text-gray-600">Sugar:</span>
                                <div className={`w-4 h-4 rounded-full ${getHFSSColor(recipe.health_scores.hfss_colors.sugar)}`}></div>
                              </div>
                              <div className="flex gap-1 items-center">
                                <span className="text-xs text-gray-600">Salt:</span>
                                <div className={`w-4 h-4 rounded-full ${getHFSSColor(recipe.health_scores.hfss_colors.salt)}`}></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {recipeSearchResults.length === 0 && !isLoadingRecipes && (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">🔍 Search for recipes to get started</p>
                  <p className="text-sm mt-2">Try searching for "macaroni cheese", "chicken curry", or "chocolate cake"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recipe Detail Dialog */}
      {showRecipeDetailDialog && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-purple-900">{selectedRecipe.name}</h3>
                <button
                  onClick={() => {
                    setShowRecipeDetailDialog(false);
                    setSelectedRecipe(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Recipe Image */}
              <img
                src={selectedRecipe.image}
                alt={selectedRecipe.name}
                className="w-full h-64 object-cover rounded-lg mb-4"
              />

              {/* Recipe Meta */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm text-gray-600">Source:</span>
                  <p className="font-semibold">{selectedRecipe.source}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm text-gray-600">Rating:</span>
                  <p className="font-semibold">⭐ {selectedRecipe.rating} ({selectedRecipe.reviews} reviews)</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm text-gray-600">Servings:</span>
                  <p className="font-semibold">🍽️ {selectedRecipe.yield}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm text-gray-600">Time:</span>
                  <p className="font-semibold">⏱️ {selectedRecipe.totalTime} min</p>
                </div>
              </div>

              {/* Health Scores */}
              {selectedRecipe.health_scores && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold mb-3">📊 Health Analysis</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-sm text-gray-600">NPM Score:</span>
                      <div className={`mt-1 px-3 py-2 rounded-lg font-bold ${
                        selectedRecipe.health_scores.npm_color === 'green' ? 'bg-green-200 text-green-900' :
                        selectedRecipe.health_scores.npm_color === 'amber' ? 'bg-amber-200 text-amber-900' :
                        'bg-red-200 text-red-900'
                      }`}>
                        {selectedRecipe.health_scores.npm_label} ({selectedRecipe.health_scores.npm_score})
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Processing Level:</span>
                      <div className={`mt-1 px-3 py-2 rounded-lg font-bold ${
                        selectedRecipe.health_scores.nova_score <= 2 ? 'bg-green-200 text-green-900' :
                        selectedRecipe.health_scores.nova_score === 3 ? 'bg-amber-200 text-amber-900' :
                        'bg-red-200 text-red-900'
                      }`}>
                        NOVA {selectedRecipe.health_scores.nova_score}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <span className="text-sm text-gray-600">HFSS Traffic Lights:</span>
                    <div className="flex gap-4 mt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Fat:</span>
                        <div className={`w-6 h-6 rounded-full ${getHFSSColor(selectedRecipe.health_scores.hfss_colors.fat)}`}></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Sugar:</span>
                        <div className={`w-6 h-6 rounded-full ${getHFSSColor(selectedRecipe.health_scores.hfss_colors.sugar)}`}></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Salt:</span>
                        <div className={`w-6 h-6 rounded-full ${getHFSSColor(selectedRecipe.health_scores.hfss_colors.salt)}`}></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Carbs:</span>
                        <div className={`w-6 h-6 rounded-full ${getHFSSColor(selectedRecipe.health_scores.hfss_colors.carbs)}`}></div>
                      </div>
                    </div>
                  </div>

                  {selectedRecipe.health_scores.optimization_potential > 0 && (
                    <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-sm text-yellow-800">
                      💡 Optimization potential: {selectedRecipe.health_scores.optimization_potential}% improvement possible
                    </div>
                  )}
                </div>
              )}

              {/* Ingredients */}
              <div className="mb-4">
                <h4 className="font-semibold mb-2">📝 Ingredients ({selectedRecipe.ingredients.length})</h4>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <ul className="space-y-1">
                    {selectedRecipe.ingredients.map((ing, idx) => (
                      <li key={idx} className="text-sm">
                        • {ing.quantity} {ing.unit} {ing.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleSaveRecipe}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  💾 Save Recipe
                </Button>
                <Button
                  onClick={handleStartCustomization}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  🎚️ Customize
                </Button>
                <Button
                  onClick={() => window.open(selectedRecipe.url, '_blank')}
                  variant="outline"
                  className="flex-1"
                >
                  🔗 View Source
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customization Dialog */}
      {showCustomizeDialog && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-purple-900">🎚️ Customize Recipe</h3>
                  <p className="text-sm text-gray-600">{selectedRecipe.name}</p>
                </div>
                <button
                  onClick={() => {
                    setShowCustomizeDialog(false);
                    setCustomizationSliders({fat: 0, sugar: 0, salt: 0, carbs: 0, additives: 0});
                    setSubstitutionResults(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Targets Button */}
              <div className="mb-4">
                <Button
                  onClick={() => setShowTargetsDialog(true)}
                  variant="outline"
                  size="sm"
                  className="text-purple-600 border-purple-300"
                >
                  🎯 Set Default Targets
                </Button>
              </div>

              {/* Sliders */}
              <div className="space-y-6 mb-6">
                {/* Fat Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="font-semibold flex items-center gap-2">
                      🧈 Saturated Fat Reduction
                      <div className={`w-4 h-4 rounded-full ${getHFSSColor(selectedRecipe.health_scores?.hfss_colors?.fat || 'gray')}`}></div>
                    </label>
                    <span className="text-lg font-bold text-purple-600">{customizationSliders.fat}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={customizationSliders.fat}
                    onChange={(e) => setCustomizationSliders({...customizationSliders, fat: parseInt(e.target.value)})}
                    className={`w-full h-3 rounded-lg appearance-none cursor-pointer ${getSliderColor('fat', customizationSliders.fat)}`}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>No change</span>
                    <span>Maximum reduction</span>
                  </div>
                </div>

                {/* Sugar Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="font-semibold flex items-center gap-2">
                      🍬 Sugar Reduction
                      <div className={`w-4 h-4 rounded-full ${getHFSSColor(selectedRecipe.health_scores?.hfss_colors?.sugar || 'gray')}`}></div>
                    </label>
                    <span className="text-lg font-bold text-purple-600">{customizationSliders.sugar}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={customizationSliders.sugar}
                    onChange={(e) => setCustomizationSliders({...customizationSliders, sugar: parseInt(e.target.value)})}
                    className={`w-full h-3 rounded-lg appearance-none cursor-pointer ${getSliderColor('sugar', customizationSliders.sugar)}`}
                  />
                </div>

                {/* Salt Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="font-semibold flex items-center gap-2">
                      🧂 Salt Reduction
                      <div className={`w-4 h-4 rounded-full ${getHFSSColor(selectedRecipe.health_scores?.hfss_colors?.salt || 'gray')}`}></div>
                    </label>
                    <span className="text-lg font-bold text-purple-600">{customizationSliders.salt}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={customizationSliders.salt}
                    onChange={(e) => setCustomizationSliders({...customizationSliders, salt: parseInt(e.target.value)})}
                    className={`w-full h-3 rounded-lg appearance-none cursor-pointer ${getSliderColor('salt', customizationSliders.salt)}`}
                  />
                </div>

                {/* Carbs Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="font-semibold flex items-center gap-2">
                      🍞 Carbs Reduction
                      <div className={`w-4 h-4 rounded-full ${getHFSSColor(selectedRecipe.health_scores?.hfss_colors?.carbs || 'gray')}`}></div>
                    </label>
                    <span className="text-lg font-bold text-purple-600">{customizationSliders.carbs}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={customizationSliders.carbs}
                    onChange={(e) => setCustomizationSliders({...customizationSliders, carbs: parseInt(e.target.value)})}
                    className={`w-full h-3 rounded-lg appearance-none cursor-pointer ${getSliderColor('carbs', customizationSliders.carbs)}`}
                  />
                </div>

                {/* Additives (NOVA) Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="font-semibold flex items-center gap-2">
                      🏭 Processing Level (NOVA)
                      <span className="text-xs text-gray-600">Current: {selectedRecipe.health_scores?.nova_score || '?'}</span>
                    </label>
                    <span className="text-lg font-bold text-purple-600">{customizationSliders.additives}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={customizationSliders.additives}
                    onChange={(e) => setCustomizationSliders({...customizationSliders, additives: parseInt(e.target.value)})}
                    className={`w-full h-3 rounded-lg appearance-none cursor-pointer ${
                      customizationSliders.additives >= 50 ? 'bg-green-200 border-green-400' : 
                      customizationSliders.additives >= 25 ? 'bg-amber-200 border-amber-400' : 
                      'bg-red-200 border-red-400'
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Higher reduction = fewer processed ingredients</p>
                </div>
              </div>

              {/* Go Button */}
              <div className="mb-6">
                <Button
                  onClick={handleApplyCustomization}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-lg py-3"
                  disabled={Object.values(customizationSliders).every(v => v === 0)}
                >
                  🚀 Generate Substitutions
                </Button>
              </div>

              {/* Substitution Results */}
              {substitutionResults && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border-2 border-green-300">
                  <h4 className="text-xl font-bold mb-4 text-green-900">✅ Optimization Results</h4>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white p-3 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Original NPM</p>
                      <p className="text-2xl font-bold text-red-600">{substitutionResults.original_npm}</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center">
                      <p className="text-sm text-gray-600">New NPM</p>
                      <p className="text-2xl font-bold text-green-600">{substitutionResults.new_npm_score}</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Improvement</p>
                      <p className="text-2xl font-bold text-blue-600">+{substitutionResults.improvement}</p>
                    </div>
                  </div>

                  <h5 className="font-semibold mb-3">🔄 Suggested Substitutions ({substitutionResults.suggestions?.length || 0})</h5>
                  <div className="space-y-3">
                    {(substitutionResults.suggestions || []).map((sub, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-bold text-lg">{sub.original_ingredient} → {sub.suggested_ingredient}</p>
                            <p className="text-sm text-gray-600">Replace {sub.quantity_to_replace}% to achieve {sub.reduction_percent}% {sub.target} reduction</p>
                          </div>
                          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                            NOVA -{sub.nova_improvement}
                          </div>
                        </div>
                        <div className="text-sm text-gray-700 mt-2">
                          <span className="font-medium">Flavor Match:</span> {sub.shared_flavors.join(', ')}
                        </div>
                        <div className="text-sm text-gray-600 mt-1 italic">
                          💡 {sub.rationale}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    <Button
                      onClick={handleSaveRecipe}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      💾 Save Customized Recipe
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Targets Dialog */}
      {showTargetsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">🎯 Set Default Reduction Targets</h3>
              <p className="text-sm text-gray-600 mb-4">
                Set your preferred default reduction percentages. These will auto-apply when customizing recipes.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">🧈 Fat Target (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={userTargets.fat_target}
                    onChange={(e) => setUserTargets({...userTargets, fat_target: parseInt(e.target.value) || 0})}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">🍬 Sugar Target (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={userTargets.sugar_target}
                    onChange={(e) => setUserTargets({...userTargets, sugar_target: parseInt(e.target.value) || 0})}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">🧂 Salt Target (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={userTargets.salt_target}
                    onChange={(e) => setUserTargets({...userTargets, salt_target: parseInt(e.target.value) || 0})}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">🍞 Carbs Target (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={userTargets.carbs_target}
                    onChange={(e) => setUserTargets({...userTargets, carbs_target: parseInt(e.target.value) || 0})}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">🏭 Processing Target (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={userTargets.additives_target}
                    onChange={(e) => setUserTargets({...userTargets, additives_target: parseInt(e.target.value) || 0})}
                    className="w-full p-2 border rounded"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleSaveTargets}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  💾 Save Targets
                </Button>
                <Button
                  onClick={() => setShowTargetsDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;